import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Input, FormGroup, Label, Alert } from 'reactstrap';
import DatePicker from 'react-datepicker';
import config from '../../config';
import { useGoogleMaps } from '../../context/GoogleMapsProvider';
import { calculateB2bBookingPrice } from '../../utils/b2bPricing';
import { discountFareAboveBase } from '../../utils/discountFloor';
import styles from './NewBooking.module.scss';
import 'react-datepicker/dist/react-datepicker.css';

const FLEX_DISCOUNT_PERCENT = 25;
const FIRST_BOOKING_DISCOUNT_PERCENT = 25;

const PART_LOAD_BOX_REMARK_TAG_REGEX = /\[PART_LOAD_BOX_COUNT:\s*\d+\]/gi;

const upsertPartLoadBoxRemark = (remarksText, boxCount) => {
  const cleaned = String(remarksText || '').replace(PART_LOAD_BOX_REMARK_TAG_REGEX, '').trim();
  const tag = `[PART_LOAD_BOX_COUNT:${boxCount}]`;
  return cleaned ? `${cleaned} ${tag}` : tag;
};

const normalizeDiscountPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(100, Math.max(0, parsed));
};

const applyDiscountToAmount = (amount, discountPercent, tollAmount = 0) => {
  const total = Number(amount);
  const toll = Number(tollAmount);
  if (!Number.isFinite(total)) return 0;
  const pct = normalizeDiscountPercent(discountPercent);
  if (pct <= 0) return total;
  const baseFare = total - toll;
  const discountedBase = baseFare * (1 - pct / 100);
  return Math.ceil(discountedBase + toll);
};

const quoteVehiclePrice = (vehicleOption, discountPercent, isB2b, hasGstin = false) => {
  if (!vehicleOption) return null;
  const pct = normalizeDiscountPercent(discountPercent);
  if (isB2b) {
    const priced = calculateB2bBookingPrice(vehicleOption, pct);
    return {
      grandTotal: priced.grandTotal,
      discountPercentage: priced.discountPercentage,
      requestedDiscountPercentage: pct,
      gstAmount: priced.gstAmount,
      discountAmount: priced.discountAmount,
      cappedAtBase: priced.cappedAtBase,
      basePrice: priced.basePrice,
    };
  }
  const toll = parseFloat(vehicleOption.toll_price_applied || 0);
  const quoted = discountFareAboveBase(
    parseFloat(vehicleOption.estimated_price),
    pct,
    toll,
    parseFloat(vehicleOption.base_price) || 0,
  );
  const preGst = quoted.total;
  if (hasGstin) {
    const grandTotal = Math.ceil(preGst * 1.05);
    return {
      grandTotal,
      discountPercentage: quoted.cappedAtBase ? quoted.effectivePercent : (quoted.discountAmount > 0 ? pct : 0),
      requestedDiscountPercentage: pct,
      gstAmount: grandTotal - preGst,
      discountAmount: quoted.discountAmount,
      cappedAtBase: quoted.cappedAtBase,
      basePrice: parseFloat(vehicleOption.base_price) || 0,
    };
  }
  return {
    grandTotal: preGst,
    discountPercentage: quoted.cappedAtBase ? quoted.effectivePercent : (quoted.discountAmount > 0 ? pct : 0),
    requestedDiscountPercentage: pct,
    gstAmount: 0,
    discountAmount: quoted.discountAmount,
    cappedAtBase: quoted.cappedAtBase,
    basePrice: parseFloat(vehicleOption.base_price) || 0,
  };
};

const computePaymentEstimate = (vehicleOption, discountPercent, isB2b, hasGstin = false) => {
  return quoteVehiclePrice(vehicleOption, discountPercent, isB2b, hasGstin)?.grandTotal || 0;
};

const formatVehicleListPrice = (vehicleOption, discountPercent, isB2b, hasGstin = false) => {
  const total = computePaymentEstimate(vehicleOption, discountPercent, isB2b, hasGstin);
  return Number(total).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const computeOriginalPrice = (vehicleOption, isB2b, hasGstin = false) => {
  if (!vehicleOption) return 0;
  if (isB2b) {
    return calculateB2bBookingPrice(vehicleOption, 0).grandTotal;
  }
  const base = parseFloat(vehicleOption.estimated_price || 0);
  if (hasGstin) {
    return Math.ceil(base * 1.05);
  }
  return base;
};

const computeFlexPrice = (vehicleOption, isB2b, hasGstin = false) => {
  return computePaymentEstimate(vehicleOption, FLEX_DISCOUNT_PERCENT, isB2b, hasGstin);
};

const getPriceBreakdown = (vehicleOption, discountPercent, isB2b, hasGstin = false) => {
  return quoteVehiclePrice(vehicleOption, discountPercent, isB2b, hasGstin);
};

/** Return the effective final amount: manual override if numeric, else computed. */
const getEffectiveAmount = (computedAmount, override) => {
  const parsed = parseFloat(override);
  if (override !== '' && Number.isFinite(parsed) && parsed >= 0) return parsed;
  return computedAmount;
};

const withServiceability = (address, data) => {
  if (!address) return address;
  return {
    ...address,
    serviceable: data?.serviceable ?? address.serviceable,
  };
};

const isNonServiceableAddress = (address) => address?.serviceable === false;

// Vehicle class to name mapping
const VEHICLE_CLASS_MAP = {
  1: "Goods Auto",
  2: "Tata Ace",
  3: "Mahindra Bolero Pickup",
  4: "Bada Dost",
  5: "Tata 407",
  6: "Ashok Leyland Partner",
  7: "Eicher Pro",
  8: "Tata 709",
  9: "Ashok Leyland Boss",
  10: "Tata LPT",
  11: "Mahindra Furio 17",
  12: "Eicher Pro 6032"
};

// Tipper vehicle class to name mapping (classes 61-67)
const TIPPER_CLASS_MAP = {
  61: "Mahindra Furio 7 Tipper",
  62: "Tata 912 LPK",
  63: "Ashok Leyland AVTR 1920",
  64: "Ashok Leyland AVTR 2820",
  65: "BharatBenz 3528C",
  66: "Eicher Pro 6035T",
  67: "BharatBenz 4228RT"
};

// Hourly class to name mapping (regular vehicles + equipment)
const EQUIPMENT_CLASS_MAP = {
  1: { name: "Goods Auto", category: "truck", icon: "fa-truck" },
  2: { name: "Tata Ace", category: "truck", icon: "fa-truck" },
  3: { name: "Mahindra Bolero Pickup", category: "truck", icon: "fa-truck" },
  4: { name: "Bada Dost", category: "truck", icon: "fa-truck" },
  5: { name: "Tata 407", category: "truck", icon: "fa-truck" },
  6: { name: "Ashok Leyland Partner", category: "truck", icon: "fa-truck" },
  7: { name: "Eicher Pro", category: "truck", icon: "fa-truck" },
  8: { name: "Tata 709", category: "truck", icon: "fa-truck" },
  9: { name: "Ashok Leyland Boss", category: "truck", icon: "fa-truck" },
  10: { name: "Tata LPT", category: "truck", icon: "fa-truck" },
  11: { name: "Mahindra Furio 17", category: "truck", icon: "fa-truck" },
  12: { name: "Eicher Pro 6032", category: "truck", icon: "fa-truck" },
  13: { name: "JCB - Backhoe Loader", category: "jcb", icon: "fa-cogs" },
  14: { name: "Chota JCB", category: "jcb", icon: "fa-cogs" },
  15: { name: "Mini Excavator", category: "jcb", icon: "fa-cogs" },
  16: { name: "Excavator", category: "jcb", icon: "fa-cogs" },
  17: { name: "Crane - 12 Ton", category: "crane", icon: "fa-industry" },
  18: { name: "Crane - 14 Ton", category: "crane", icon: "fa-industry" },
  19: { name: "Crane - 15 Ton", category: "crane", icon: "fa-industry" },
};

const FULL_DAY_HOURS = 8;

const DURATION_PRESETS = [
  { label: '1 Hour', value: 1 },
  { label: '2 Hours', value: 2 },
  { label: '3 Hours', value: 3 },
  { label: '4 Hours', value: 4 },
  { label: 'Half Day (4h)', value: 4 },
  { label: 'Full Day (8h)', value: 8 },
];

const mapContainerStyle = {
  width: '100%',
  height: '450px'
};

const mapStyles = [
  {
    featureType: 'all',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#dadada' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#616161' }]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9e9e9e' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c9c9c9' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9e9e9e' }]
  }
];

const defaultCenter = {
  lat: 12.9716, // Bangalore default
  lng: 77.5946
};

const NewBooking = ({
  compactMode = false,
  prefillUserId = null,
  prefillCustomerId = null,
  prefillUserName = null,
  prefillUserPhone = null,
  onBookingCreated = null
}) => {
  const history = useHistory();
  const location = useLocation();

  // Check if we're in modify mode or draft mode
  const isModifyMode = location.pathname.includes('modify-booking');
  const modifyBookingData = location.state?.booking;
  const isDraftMode = isModifyMode && (modifyBookingData?.state?.toLowerCase?.() === 'draft' || location.state?.isDraft);
  const [bookingIdToModify, setBookingIdToModify] = useState(
    modifyBookingData?.id ?? modifyBookingData?.booking_id ?? null
  );

  // Keep booking id in sync when route state changes.
  useEffect(() => {
    if (isModifyMode) {
      setBookingIdToModify(modifyBookingData?.id ?? modifyBookingData?.booking_id ?? null);
    }
  }, [isModifyMode, modifyBookingData]);

  // Helper: format JS Date to local YYYY-MM-DDTHH:MM:SS (shared across handlers)
  const formatLocalDateTime = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const [pickupAddress, setPickupAddress] = useState(null);
  const [dropAddresses, setDropAddresses] = useState([null]);
  const [pickupMarker, setPickupMarker] = useState(null);
  const [dropMarkers, setDropMarkers] = useState([]);
  const [activeMarkerIndex, setActiveMarkerIndex] = useState(null); // -1 for pickup, 0+ for drops
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [recommendedAddresses, setRecommendedAddresses] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userSearch, setUserSearch] = useState(prefillUserName || '');
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(
    prefillUserId ? {
      id: prefillUserId,
      name: prefillUserName,
      phone_number: prefillUserPhone
    } : null
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState(prefillCustomerId);
  const [sourceLeadId, setSourceLeadId] = useState(null); // Lead ID when converting from leads tab
  const [customerDiscountPercent, setCustomerDiscountPercent] = useState(0);
  const [customerBookingCount, setCustomerBookingCount] = useState(null);
  const [isB2bCustomer, setIsB2bCustomer] = useState(false);
  const [customerGstin, setCustomerGstin] = useState('');
  const [bookingDiscountPercent, setBookingDiscountPercent] = useState(
    isModifyMode && modifyBookingData?.discount_factor ? String(modifyBookingData.discount_factor) : ''
  );
  const [manualAmountOverride, setManualAmountOverride] = useState('');

  // Set pickup time to 15 minutes from now by default (or from booking if modifying)
  const getDefaultPickupTime = () => {
    if (isModifyMode && modifyBookingData?.pickup_time) {
      return new Date(modifyBookingData.pickup_time);
    }
    const now = new Date();
    now.setMinutes(now.getMinutes() + 15);
    return now;
  };

  const [pickupTime, setPickupTime] = useState(getDefaultPickupTime()); // State for pickup time
  const [vehicleType, setVehicleType] = useState(''); // Vehicle selection
  const [loadWeight, setLoadWeight] = useState('');
  const [loadLength, setLoadLength] = useState('');
  const [loadWidth, setLoadWidth] = useState('');
  const [loadHeight, setLoadHeight] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);

  // Booking mode tab: 'standard' | 'hourly' | 'part_load'
  const [bookingMode, setBookingMode] = useState('standard');

  // Flex booking toggle (standard full-load only)
  const [isFlexBooking, setIsFlexBooking] = useState(
    isModifyMode && String(modifyBookingData?.booking_type || '').toLowerCase() === 'flex'
  );

  // Part-load state
  const [partLoadBoxCount, setPartLoadBoxCount] = useState('1');

  // Hourly booking state
  const [selectedEquipmentClass, setSelectedEquipmentClass] = useState(null);
  const [equipmentDuration, setEquipmentDuration] = useState('');
  const [equipmentPriceEstimate, setEquipmentPriceEstimate] = useState(null);
  const [hourlyPricingLoading, setHourlyPricingLoading] = useState(false);

  const isPartLoadMode = !isModifyMode && bookingMode === 'part_load';

  // Additional booking fields
  const [deliveryDeadline, setDeliveryDeadline] = useState(
    isModifyMode && modifyBookingData?.delivery_deadline ? new Date(modifyBookingData.delivery_deadline) : null
  );
  const [remarks, setRemarks] = useState(isModifyMode && modifyBookingData?.remarks ? modifyBookingData.remarks : '');
  const [materialDescription, setMaterialDescription] = useState(isModifyMode && modifyBookingData?.material_description ? modifyBookingData.material_description : '');
  const wasNewBookingRouteActiveRef = useRef(false);

  // Confirmation popup state
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmPopupType, setConfirmPopupType] = useState(null); // 'confirm' | 'modify' | 'confirm_draft' | 'confirm_hourly' | 'modify_hourly'

  // Initialize modify mode data
  useEffect(() => {
    const initializeModifyMode = async () => {
      if (isModifyMode && modifyBookingData) {
        console.log('Modify mode initialized with booking:', modifyBookingData);
        console.log('Pickup address ID:', modifyBookingData.pickup_address_id);
        console.log('Drop address IDs:', modifyBookingData.drop_address_ids);

        // Prefill user data
        if (modifyBookingData.customer_name || modifyBookingData.customer_phone) {
          setUserSearch(`${modifyBookingData.customer_name || ''} (${modifyBookingData.customer_phone || ''})`);
          setSelectedCustomerId(modifyBookingData.customer_id);
          setSelectedUser({
            id: modifyBookingData.customer_id,
            name: modifyBookingData.customer_name,
            phone_number: modifyBookingData.customer_phone
          });
        }

        // Fetch and prefill pickup address
        if (modifyBookingData.pickup_address_id) {
          try {
            console.log('Fetching pickup address from:', `${config.customerApiBase}/addresses/${modifyBookingData.pickup_address_id}`);
            const response = await fetch(`${config.customerApiBase}/addresses/${modifyBookingData.pickup_address_id}`);
            console.log('Pickup address response status:', response.status);
            if (response.ok) {
              const addressData = await response.json();
              console.log('Pickup address data:', addressData);
              const addressObj = {
                id: addressData.id,
                text: `${addressData.locality || ''}, ${addressData.city || ''}`.trim(),
                lat: addressData.lat,
                lng: addressData.lng,
                full: addressData
              };
              console.log('Setting pickup address:', addressObj);
              setPickupAddress(addressObj);
              if (addressData.lat && addressData.lng) {
                const markerPos = { lat: addressData.lat, lng: addressData.lng };
                setPickupMarker(markerPos);

                // Center map on pickup location after a brief delay to ensure map is loaded
                setTimeout(() => {
                  if (mapRef.current) {
                    mapRef.current.panTo(markerPos);
                    mapRef.current.setZoom(13);
                  }
                }, 500);
              }
            } else {
              console.error('Failed to fetch pickup address:', response.status, await response.text());
            }
          } catch (err) {
            console.error('Error fetching pickup address:', err);
          }
        }

        // Fetch and prefill drop addresses
        if (modifyBookingData.drop_address_ids && modifyBookingData.drop_address_ids.length > 0) {
          console.log('Fetching drop addresses for IDs:', modifyBookingData.drop_address_ids);
          const dropAddressPromises = modifyBookingData.drop_address_ids.map(async (addressId) => {
            try {
              console.log('Fetching drop address from:', `${config.customerApiBase}/addresses/${addressId}`);
              const response = await fetch(`${config.customerApiBase}/addresses/${addressId}`);
              console.log('Drop address response status:', response.status, 'for ID:', addressId);
              if (response.ok) {
                const addressData = await response.json();
                console.log('Drop address data:', addressData);
                return {
                  id: addressData.id,
                  text: `${addressData.locality || ''}, ${addressData.city || ''}`.trim(),
                  lat: addressData.lat,
                  lng: addressData.lng,
                  full: addressData
                };
              } else {
                console.error('Failed to fetch drop address:', response.status, await response.text());
              }
            } catch (err) {
              console.error('Error fetching drop address:', err);
              return null;
            }
          });

          const resolvedDropAddresses = await Promise.all(dropAddressPromises);
          console.log('Resolved drop addresses:', resolvedDropAddresses);
          const validDropAddresses = resolvedDropAddresses.filter(addr => addr !== null);

          if (validDropAddresses.length > 0) {
            console.log('Setting drop addresses:', validDropAddresses);
            setDropAddresses(validDropAddresses);
            const markers = validDropAddresses.map(addr =>
              addr.lat && addr.lng ? { lat: addr.lat, lng: addr.lng } : null
            ).filter(m => m !== null);
            setDropMarkers(markers);
          }
        }

        // Prefill vehicle class - map class ID to vehicle name
        if (modifyBookingData.vehicle_class_id) {
          console.log('Vehicle class ID from booking:', modifyBookingData.vehicle_class_id, 'type:', typeof modifyBookingData.vehicle_class_id);
          const classId = parseInt(modifyBookingData.vehicle_class_id);
          const vehicleName = VEHICLE_CLASS_MAP[classId];
          if (vehicleName) {
            console.log('Found vehicle name for class', classId, ':', vehicleName);
            setVehicleType(vehicleName);
          } else {
            console.warn('No vehicle name found for class:', classId);
            console.log('Available classes:', Object.keys(VEHICLE_CLASS_MAP));
          }
        }
      }
    };

    initializeModifyMode();
  }, [isModifyMode, modifyBookingData]);

  // Auto-determine booking type based on pickup time and flex mode
  const getBookingType = (pickupDateTime) => {
    if (isFlexBooking) return 'flex';
    const now = new Date();
    const timeDiff = pickupDateTime.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60); // Convert to hours

    return hoursDiff > 2 ? 'scheduled' : 'instant';
  };

  const getBookingTypeBadgeClass = () => {
    if (isPartLoadMode) return styles.part;
    const type = getBookingType(pickupTime);
    if (type === 'flex') return styles.flex;
    if (type === 'instant') return styles.instant;
    return styles.scheduled;
  };

  const fetchHourlyPricingEstimate = useCallback(async (classId, hours, pickupAddressId) => {
    if (!classId || !hours || hours <= 0 || !pickupAddressId) return null;

    const response = await fetch(`${config.customerApiBase}/hourly-pricing-estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pickup_address_id: parseInt(pickupAddressId),
        vehicle_class_id: parseInt(classId),
        duration_hours: hours,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || 'Failed to fetch hourly pricing estimate');
    }

    const data = await response.json();

    return {
      base_price: data.base_price,
      rate_per_hour: data.rate_per_hour,
      total: data.payment_estimate,
      is_full_day: data.is_full_day,
      full_day_price: data.full_day_price,
      pricing_source: data.pricing_source,
    };
  }, []);

  // Recalculate equipment price from backend when class, duration, or pickup address changes.
  useEffect(() => {
    let cancelled = false;

    const updateHourlyPricing = async () => {
      if (bookingMode !== 'hourly' || !selectedEquipmentClass || !equipmentDuration || !pickupAddress?.id) {
        setEquipmentPriceEstimate(null);
        return;
      }

      const hours = parseFloat(equipmentDuration);
      if (!hours || hours <= 0) {
        setEquipmentPriceEstimate(null);
        return;
      }

      setHourlyPricingLoading(true);
      try {
        const estimate = await fetchHourlyPricingEstimate(selectedEquipmentClass, hours, pickupAddress.id);
        if (!cancelled) {
          setEquipmentPriceEstimate(estimate);
        }
      } catch (err) {
        if (!cancelled) {
          setEquipmentPriceEstimate(null);
        }
      } finally {
        if (!cancelled) {
          setHourlyPricingLoading(false);
        }
      }
    };

    updateHourlyPricing();
    return () => {
      cancelled = true;
    };
  }, [bookingMode, selectedEquipmentClass, equipmentDuration, pickupAddress?.id, fetchHourlyPricingEstimate]);

  // Clear stale vehicle pricing/selection when switching booking modes.
  useEffect(() => {
    setVehicleOptions([]);
    setSelectedVehicle(null);
    setError(null);
  }, [bookingMode]);

  // Handle hourly booking confirmation
  const handleConfirmHourlyBooking = async () => {
    if (!selectedUser || !selectedCustomerId || !pickupAddress || !selectedEquipmentClass || !equipmentDuration) {
      setError('Please complete all required fields: user, location, vehicle/equipment type, and duration');
      return;
    }

    const hours = parseFloat(equipmentDuration);
    if (isNaN(hours) || hours <= 0) {
      setError('Please enter a valid duration in hours');
      return;
    }

    const now = new Date();
    if (pickupTime <= now) {
      setError('Pickup time has already passed. Please select a future time.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const priceData = await fetchHourlyPricingEstimate(selectedEquipmentClass, hours, pickupAddress.id);
      if (!priceData) {
        throw new Error('Failed to calculate pricing');
      }

      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hh}:${mm}:${ss}`;
      };

      const bookingData = {
        customer_id: parseInt(selectedCustomerId),
        pickup_time: formatLocalDateTime(pickupTime),
        pickup_address_id: parseInt(pickupAddress.id),
        drop_address_ids: [parseInt(pickupAddress.id)], // Same as pickup for hourly
        vehicle_class_id: parseInt(selectedEquipmentClass),
        remarks: remarks || '',
        material_description: materialDescription.trim() || null,
        delivery_deadline: null,
        booking_type: 'hourly',
        state: 'Pending',
        distance: 0.0,
        duration: Math.round(hours * 60), // Convert hours to minutes
        toll_charge: 0.0,
        payment_estimate: getEffectiveAmount(applyDiscountToAmount(priceData.total, bookingDiscountPercent, 0.0), manualAmountOverride),
        payment_id: null,
        rate_per_hour: priceData.rate_per_hour,
        discount_factor: normalizeDiscountPercent(bookingDiscountPercent),
        source: 'ops',
        source_lead_id: sourceLeadId,
      };

      const response = await fetch(`${config.customerApiBase}/create-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to create hourly booking');
      }

      const data = await response.json();
      alert(`Hourly booking created successfully! Booking ID: ${data.booking_id}`);

      if (onBookingCreated) {
        onBookingCreated(data);
      } else {
        history.push('/app/bookings');
      }
    } catch (err) {
      setError('Failed to create hourly booking: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraftHourly = async () => {
    if (!selectedUser || !selectedCustomerId || !pickupAddress) {
      setError('Please select a customer and location to save a draft');
      return;
    }
    if (!selectedEquipmentClass) {
      setError('Please select a vehicle/equipment to save a draft');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const hours = parseFloat(equipmentDuration) || 0;
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hh}:${mm}:${ss}`;
      };

      const bookingData = {
        customer_id: parseInt(selectedCustomerId),
        pickup_time: formatLocalDateTime(pickupTime),
        pickup_address_id: parseInt(pickupAddress.id),
        drop_address_ids: [parseInt(pickupAddress.id)],
        vehicle_class_id: parseInt(selectedEquipmentClass) || 0,
        remarks: remarks || '',
        material_description: materialDescription.trim() || null,
        delivery_deadline: null,
        booking_type: 'hourly',
        state: 'Draft',
        distance: 0.0,
        duration: Math.round(hours * 60),
        toll_charge: 0.0,
        payment_estimate: getEffectiveAmount(equipmentPriceEstimate ? applyDiscountToAmount(equipmentPriceEstimate.total, bookingDiscountPercent, 0.0) : 0.0, manualAmountOverride),
        payment_id: null,
        rate_per_hour: equipmentPriceEstimate?.rate_per_hour || null,
        discount_factor: normalizeDiscountPercent(bookingDiscountPercent),
        source: 'ops',
        source_lead_id: sourceLeadId,
      };

      let response;
      if (isDraftMode && bookingIdToModify) {
        // Update existing draft via update-draft endpoint (designed specifically for drafts)
        response = await fetch(`${config.customerApiBase}/update-draft/${bookingIdToModify}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookingData),
        });
      } else {
        response = await fetch(`${config.customerApiBase}/draft-booking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookingData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to save draft');
      }

      const data = await response.json();
      alert(`Draft ${isDraftMode ? 'updated' : 'saved'} successfully! Draft ID: ${data.booking_id}`);

      if (onBookingCreated) {
        onBookingCreated(data);
      } else {
        history.push('/app/bookings?filter=draft');
      }
    } catch (err) {
      setError(`Failed to save draft: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const mapRef = useRef(null);

  const { isLoaded, loadError } = useGoogleMaps();

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Prefill from query params if provided (user_id, customer_id, name, phone)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('user_id');
    const customerId = params.get('customer_id');
    const name = params.get('name');
    const phone = params.get('phone');

    if (userId) {
      // selectedUser stores the users.users.id
      setSelectedUser(parseInt(userId, 10));
    }
    if (customerId) {
      setSelectedCustomerId(parseInt(customerId, 10));
    }
    if (name || phone) {
      const display = `${name || ''}${name && phone ? ' (' : ''}${phone || ''}${name && phone ? ')' : ''}`;
      setUserSearch(display.trim());
    }
  }, [location.search]);

  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    if (activeMarkerIndex === -1) {
      // Pickup location
      setPickupMarker({ lat, lng });
      resolveAndSetAddress({ lat, lng }, true);
    } else if (activeMarkerIndex >= 0) {
      // Drop location
      const newDropMarkers = [...dropMarkers];
      newDropMarkers[activeMarkerIndex] = { lat, lng };
      setDropMarkers(newDropMarkers);
      resolveAndSetAddress({ lat, lng }, false, activeMarkerIndex);
    }
  }, [activeMarkerIndex, dropMarkers]);

  const handleBack = () => {
    history.push('/app/customers');
  };

  const resolveAndSetAddress = async (location, isPickup, dropIndex) => {
    try {
      const response = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lat: location.lat,
          lng: location.lng
        })
      });

      if (!response.ok) {
        throw new Error('Failed to resolve location');
      }

      const data = await response.json();

      if (data.type === 'similar') {
        setAddressSuggestions(data.matches);
      } else {
        if (isPickup) {
          setPickupAddress(withServiceability({
            id: data.address_id,
            text: data.resolved_address,
            location: location
          }, data));
        } else {
          const newDropAddresses = [...dropAddresses];
          newDropAddresses[dropIndex] = withServiceability({
            id: data.address_id,
            text: data.resolved_address,
            location: location
          }, data);
          setDropAddresses(newDropAddresses);
        }
      }
    } catch (err) {
      setError('Failed to resolve location: ' + err.message);
    }
  };

  const handlePlaceSelect = async (isPickup, dropIndex) => {
    // Location search is handled by handleSearchLocation via backend
    await handleSearchLocation(isPickup, dropIndex);
  };

  const addDropLocation = () => {
    setDropAddresses([...dropAddresses, null]);
    setDropMarkers([...dropMarkers, null]);
  };

  const removeDropLocation = (index) => {
    const newDropAddresses = [...dropAddresses];
    const newDropMarkers = [...dropMarkers];
    newDropAddresses.splice(index, 1);
    newDropMarkers.splice(index, 1);
    setDropAddresses(newDropAddresses);
    setDropMarkers(newDropMarkers);
  };

  const handleUserSearch = async (query) => {
    setUserSearch(query);
    if (query.length < 3) {
      setUserSuggestions([]);
      return;
    }

    try {
      const response = await fetch(`${config.customerApiBase}/users/search?query=${query}`);
      if (!response.ok) {
        throw new Error('Failed to fetch user suggestions');
      }
      const data = await response.json();
      const users = data.users || [];
      setUserSuggestions(users);
      // Auto-select on exact phone match (ops often paste full number without clicking)
      const qDigits = query.replace(/\D/g, '');
      if (users.length === 1 && qDigits.length >= 10) {
        const pDigits = String(users[0].phone || '').replace(/\D/g, '');
        if (pDigits === qDigits || pDigits.endsWith(qDigits) || qDigits.endsWith(pDigits)) {
          handleUserSelect(users[0]);
        }
      }
    } catch (err) {
      setError('Failed to fetch user suggestions: ' + err.message);
    }
  };

  const handleUserSelect = (user) => {
    setUserSearch(`${user.name} (${user.phone})`);
    setUserSuggestions([]);
    setSelectedUser(user.id); // Lock the user ID (users.users.id)
    setIsB2bCustomer(Boolean(user.is_b2b));
    // Resolve user -> customer id from backend
    (async () => {
      try {
        const resp = await fetch(`${config.customerApiBase}/users/${user.id}/customer`);
        if (!resp.ok) {
          // If not found, clear any previous customer id and show message
          setSelectedCustomerId(null);
          setIsB2bCustomer(false);
          const err = await resp.json().catch(() => ({}));
          setError(err.detail || 'No customer found for selected user');
          return;
        }
        const data = await resp.json();
        setSelectedCustomerId(data.customer_id);
        setError(null);
        await fetchCustomerDiscount(data.customer_id);
      } catch (e) {
        setSelectedCustomerId(null);
        setIsB2bCustomer(false);
        setError('Failed to resolve customer for selected user: ' + e.message);
      }
    })();
  };

  const fetchCustomerDiscount = useCallback(async (customerId) => {
    if (!customerId) {
      setCustomerDiscountPercent(0);
      setCustomerBookingCount(null);
      setIsB2bCustomer(false);
      setCustomerGstin('');
      setBookingDiscountPercent('');
      return;
    }
    try {
      const resp = await fetch(`${config.customerApiBase}/customers/${customerId}/details`);
      if (!resp.ok) {
        setCustomerDiscountPercent(0);
        setCustomerBookingCount(null);
        setIsB2bCustomer(false);
        setCustomerGstin('');
        return;
      }
      const data = await resp.json();
      const pct = Number(data?.customer?.discount_percentage || 0);
      const safePct = normalizeDiscountPercent(pct);
      const bookingCount = Number(data?.customer?.booking_count ?? 0);
      const b2b = Boolean(data?.customer?.is_b2b);
      const gstin = data?.customer?.gstin || '';
      setCustomerDiscountPercent(safePct);
      setCustomerBookingCount(bookingCount);
      setIsB2bCustomer(b2b);
      setCustomerGstin(gstin);
      if (safePct > 0) {
        setBookingDiscountPercent(String(safePct));
      } else if (bookingCount === 0) {
        setBookingDiscountPercent(String(FIRST_BOOKING_DISCOUNT_PERCENT));
      } else {
        setBookingDiscountPercent('');
      }
    } catch (e) {
      setCustomerDiscountPercent(0);
      setCustomerBookingCount(null);
      setIsB2bCustomer(false);
      setCustomerGstin('');
    }
  }, []);

  useEffect(() => {
    fetchCustomerDiscount(selectedCustomerId);
  }, [selectedCustomerId, fetchCustomerDiscount]);

  // Auto-detect early-booking 10% discount when pickup is tomorrow or later (non-flex only)
  useEffect(() => {
    if (!pickupTime || isFlexBooking) return;
    // Don't override B2B, first-booking, or preloaded customer discount
    if (customerDiscountPercent > 0 || isB2bCustomer) return;
    if (customerBookingCount === 0) return;

    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const pickupDate = new Date(pickupTime);
    pickupDate.setHours(0, 0, 0, 0);

    if (pickupDate >= tomorrow) {
      const currentDiscount = normalizeDiscountPercent(bookingDiscountPercent);
      if (currentDiscount === 0) {
        setBookingDiscountPercent('10');
      }
    }
    // Note: we intentionally don't auto-clear when switching back to same-day,
    // so the user can keep a manually entered discount.
  }, [pickupTime, customerDiscountPercent, customerBookingCount, isB2bCustomer, bookingDiscountPercent, isFlexBooking]);

  // Flex booking: auto-set the best single discount (25% flex vs customer/first-booking discount)
  // when toggled on, and reset to the natural discount when toggled off.
  useEffect(() => {
    if (isFlexBooking) {
      const bestFlexDiscount = Math.max(
        FLEX_DISCOUNT_PERCENT,
        customerDiscountPercent,
        customerBookingCount === 0 ? FIRST_BOOKING_DISCOUNT_PERCENT : 0,
      );
      setBookingDiscountPercent(String(bestFlexDiscount));
    } else {
      // Restore natural discount when flex is turned off
      if (customerDiscountPercent > 0) {
        setBookingDiscountPercent(String(customerDiscountPercent));
      } else if (customerBookingCount === 0) {
        setBookingDiscountPercent(String(FIRST_BOOKING_DISCOUNT_PERCENT));
      } else {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const pickupDate = new Date(pickupTime);
        pickupDate.setHours(0, 0, 0, 0);
        if (pickupDate >= tomorrow) {
          setBookingDiscountPercent('10');
        } else {
          setBookingDiscountPercent('');
        }
      }
    }
  }, [isFlexBooking, customerDiscountPercent, customerBookingCount, pickupTime]);

  const fetchRecommendedAddresses = useCallback(async (customerId) => {
    if (!customerId) {
      setRecommendedAddresses([]);
      setRecommendationsLoading(false);
      return;
    }

    setRecommendationsLoading(true);
    try {
      const resp = await fetch(`${config.customerApiBase}/customers/${customerId}/address-recommendations`);
      if (!resp.ok) {
        setRecommendedAddresses([]);
        return;
      }

      const data = await resp.json().catch(() => ({}));
      setRecommendedAddresses(Array.isArray(data.addresses) ? data.addresses : []);
    } catch (e) {
      setRecommendedAddresses([]);
    } finally {
      setRecommendationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecommendedAddresses(selectedCustomerId);
  }, [selectedCustomerId, fetchRecommendedAddresses]);

  const clearSelectedUser = () => {
    setUserSearch('');
    setSelectedUser(null);
    setSelectedCustomerId(null);
    setRecommendedAddresses([]);
    setRecommendationsLoading(false);
    setCustomerDiscountPercent(0);
    setIsB2bCustomer(false);
    setBookingDiscountPercent('');
  };

  const getRecommendedAddressTitle = (address) => {
    const locality = String(address?.locality || address?.text || '').trim();
    if (!locality) return 'Saved address';
    return locality.split(',')[0].trim();
  };

  const getRecommendedAddressSubtitle = (address) => {
    const parts = [address?.city, address?.state, address?.pin_code].filter(Boolean);
    return parts.join(' • ');
  };

  const applyRecommendedAddress = (address, isPickup, dropIndex = null) => {
    const lat = Number(address?.latitude);
    const lng = Number(address?.longitude);
    const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);

    const location = hasCoordinates ? { lat, lng } : null;
    const displayText = address?.text || [address?.locality, address?.city].filter(Boolean).join(', ');

    if (isPickup) {
      setPickupAddress({
        id: address.id,
        text: displayText,
        location,
      });
      if (location) {
        setPickupMarker(location);
      }
    } else if (dropIndex !== null) {
      const newDropAddresses = [...dropAddresses];
      newDropAddresses[dropIndex] = {
        id: address.id,
        text: displayText,
        location,
      };
      setDropAddresses(newDropAddresses);

      if (location) {
        const newDropMarkers = [...dropMarkers];
        newDropMarkers[dropIndex] = location;
        setDropMarkers(newDropMarkers);
      }
    }

    setAddressSuggestions([]);

    if (location && mapRef.current) {
      mapRef.current.panTo(location);
      mapRef.current.setZoom(15);
    }
  };

  const handleSearchLocation = async (isPickup, dropIndex) => {
    const query = isPickup ? pickupAddress?.text : dropAddresses[dropIndex]?.text;
    if (!query) {
      setError('Please enter a location to search.');
      return;
    }

    try {
      setError(null);
      const response = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address_text: query }),
      });

      if (!response.ok) {
        throw new Error('Failed to resolve location');
      }

      const data = await response.json();

      if (data.type === 'similar') {
        // Show similar addresses for user selection (displayed inline, no error message)
        setAddressSuggestions(data.matches);
        return;
      }

      // Handle exact match or new address
      const location = {
        lat: data.latitude,
        lng: data.longitude,
      };

      if (isPickup) {
        setPickupAddress(withServiceability({
          id: data.address_id,
          text: data.resolved_address,
          location,
        }, data));
        setPickupMarker(location);
      } else {
        const newDropAddresses = [...dropAddresses];
        newDropAddresses[dropIndex] = withServiceability({
          id: data.address_id,
          text: data.resolved_address,
          location,
        }, data);
        setDropAddresses(newDropAddresses);

        const newDropMarkers = [...dropMarkers];
        newDropMarkers[dropIndex] = location;
        setDropMarkers(newDropMarkers);
      }

      // Center map on the resolved location
      if (mapRef.current) {
        mapRef.current.panTo(location);
        mapRef.current.setZoom(15);
      }

    } catch (err) {
      setError('Failed to resolve location: ' + err.message);
    }
  };

  const applyAddressSuggestion = async (suggestion, isPickup, dropIndex = null) => {
    try {
      setError(null);

      let resolvedSuggestion = suggestion;

      // Google-place suggestions may not have an address ID yet.
      // Resolve by coordinates to get canonical address_id from backend.
      if (!suggestion.id) {
        const response = await fetch(`${config.customerApiBase}/resolve-location`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lat: suggestion.latitude,
            lng: suggestion.longitude,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to resolve selected suggestion');
        }

        const data = await response.json();
        if (!data?.address_id) {
          throw new Error('Selected suggestion did not return a valid address ID');
        }

        resolvedSuggestion = {
          id: data.address_id,
          locality: data.resolved_address,
          latitude: data.latitude,
          longitude: data.longitude,
          serviceable: data.serviceable,
        };
      }

      const location = {
        lat: Number(resolvedSuggestion.latitude),
        lng: Number(resolvedSuggestion.longitude),
      };
      const resolvedServiceable = resolvedSuggestion.serviceable ?? suggestion.serviceable;

      if (isPickup) {
        setPickupAddress(withServiceability({
          id: resolvedSuggestion.id,
          text: resolvedSuggestion.locality || suggestion.locality || suggestion.resolved_address,
          location,
        }, { serviceable: resolvedServiceable }));
        setPickupMarker(location);
      } else {
        const newDropAddresses = [...dropAddresses];
        newDropAddresses[dropIndex] = withServiceability({
          id: resolvedSuggestion.id,
          text: resolvedSuggestion.locality || suggestion.locality || suggestion.resolved_address,
          location,
        }, { serviceable: resolvedServiceable });
        setDropAddresses(newDropAddresses);

        const newDropMarkers = [...dropMarkers];
        newDropMarkers[dropIndex] = location;
        setDropMarkers(newDropMarkers);
      }

      setAddressSuggestions([]);
      if (mapRef.current) {
        mapRef.current.panTo(location);
        mapRef.current.setZoom(15);
      }
    } catch (err) {
      setError('Failed to apply address suggestion: ' + err.message);
    }
  };

  const handleGetVehicleOptions = async (mode = 'vehicle') => {
    // mode: 'vehicle' | 'allClasses' | 'tipper'
    const getAllClasses = mode === 'allClasses';
    const isTipperRates = mode === 'tipper';

    if (!selectedUser || !pickupAddress || dropAddresses.some(addr => !addr)) {
      setError('Please complete all required fields: user, pickup location, and drop location(s)');
      return;
    }

    if (isPartLoadMode) {
      const parsedBoxCount = parseInt(partLoadBoxCount, 10);
      if (!Number.isFinite(parsedBoxCount) || parsedBoxCount < 1) {
        setError('Please enter a valid number of boxes (minimum 1) for part-load booking');
        return;
      }
    } else {
      // Check that at least one vehicle parameter is provided
      const hasVehicleParams = vehicleType || loadWeight || loadLength || loadWidth || loadHeight || bodyType;
      if (!getAllClasses && !isTipperRates && !hasVehicleParams) {
        setError('Please specify at least one vehicle requirement: vehicle type, load dimensions, or body type');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Format pickup time in local timezone as YYYY-MM-DDTHH:MM:SS
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const requestBody = {
        pickup_address_id: String(pickupAddress.id),  // Convert to string
        drop_address_ids: dropAddresses.map(addr => String(addr.id)),  // Convert to string array
        pickup_time: formatLocalDateTime(pickupTime), // Send local time in proper format
        source: 'ops',
        bypass_distance_check: true,
      };

      if (isPartLoadMode) {
        requestBody.booking_type = 'part';
        requestBody.vehicle_name = 'part_load';
        requestBody.box_count = parseInt(partLoadBoxCount, 10);
      } else {
        if (getAllClasses) {
          requestBody.all_classes_rates = true;
        }
        if (isTipperRates) {
          requestBody.tipper_rates = true;
        }

        // Add vehicle parameters if provided
        if (vehicleType) requestBody.vehicle_name = vehicleType;
        if (loadWeight) requestBody.load_weight = parseFloat(loadWeight);
        if (loadLength) requestBody.load_length = parseFloat(loadLength);
        if (loadWidth) requestBody.load_width = parseFloat(loadWidth);
        if (loadHeight) requestBody.load_height = parseFloat(loadHeight);
        if (bodyType) requestBody.body_type = bodyType;
      }

      const response = await fetch(`${config.customerApiBase}/resolve-vehicle-pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get vehicle options');
      }

      const data = await response.json();

      if (data.status === 'ok') {
        const options = data.vehicle_options || [];
        if (options.length === 0) {
          setVehicleOptions([]);
          setSelectedVehicle(null);
          setError(data.message || 'No vehicles are currently available for this route. Please try a different pickup or drop location, or contact support if this persists.');
          return;
        }
        setVehicleOptions(options);
        if (isPartLoadMode && options.length > 0) {
          setSelectedVehicle(0); // Auto-select part-load option
        } else {
          setSelectedVehicle(null); // Reset selection
        }
      } else if (data.status === 'needs_deferred_handoff') {
        setVehicleOptions([]);
        setSelectedVehicle(null);
        setError(
          data.message || 'This route is outside our standard service area. Please contact support for assistance with this booking.'
        );
      } else {
        setError(data.message || 'Failed to get vehicle options');
      }

    } catch (err) {
      setError('Failed to get vehicle options: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async () => {
    const selectedVehicleIndex = isPartLoadMode
      ? (selectedVehicle !== null ? selectedVehicle : (vehicleOptions.length > 0 ? 0 : null))
      : selectedVehicle;

    if (!selectedUser || !selectedCustomerId || !pickupAddress || dropAddresses.some(addr => !addr) || selectedVehicleIndex === null) {
      setError('Please complete all required fields before confirming the booking');
      return;
    }

    // Validate pickup time is not in the past
    const now = new Date();
    if (pickupTime <= now) {
      setError('Pickup time has already passed. Please select a future time.');
      return;
    }

    // Additional validation for modify mode - minimum 20 minutes in future
    if (isModifyMode) {
      const timeDiffMinutes = (pickupTime - now) / (1000 * 60);
      if (timeDiffMinutes < 20) {
        setError('For modification, pickup time must be at least 20 minutes in the future.');
        return;
      }
    }

    // Flex booking validation
    if (isFlexBooking) {
      if (!deliveryDeadline) {
        setError('Please select a latest pickup time for the flex booking.');
        return;
      }
      if (deliveryDeadline <= pickupTime) {
        setError('Latest pickup time must be after the earliest pickup time.');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const selectedVehicleData = vehicleOptions[selectedVehicleIndex];
      const priceBreakdown = getPriceBreakdown(
        selectedVehicleData,
        bookingDiscountPercent,
        isB2bCustomer,
        !!customerGstin,
      );
      const appliedDiscountFactor = priceBreakdown?.discountPercentage || 0;
      const discountedEstimate = priceBreakdown?.grandTotal || 0;

      let normalizedRemarks = remarks || '';
      let normalizedPartLoadBoxCount = null;
      if (isPartLoadMode) {
        const parsedBoxCount = parseInt(partLoadBoxCount, 10);
        if (!Number.isFinite(parsedBoxCount) || parsedBoxCount < 1) {
          throw new Error('Please enter a valid number of boxes (minimum 1)');
        }
        normalizedPartLoadBoxCount = parsedBoxCount;
        normalizedRemarks = upsertPartLoadBoxRemark(normalizedRemarks, parsedBoxCount);
      }

      // Format pickup time in local timezone as YYYY-MM-DDTHH:MM:SS
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const bookingData = {
        customer_id: parseInt(selectedCustomerId),
        pickup_time: formatLocalDateTime(pickupTime),
        pickup_address_id: parseInt(pickupAddress.id),
        drop_address_ids: dropAddresses.map(addr => parseInt(addr.id)),
        vehicle_class_id: parseInt(selectedVehicleData.vehicle_class),
        remarks: normalizedRemarks,
        material_description: materialDescription.trim() || null,
        delivery_deadline: deliveryDeadline ? formatLocalDateTime(deliveryDeadline) : null,
        booking_type: isPartLoadMode ? 'part' : getBookingType(pickupTime), // Auto-determine based on pickup time
        state: 'Pending',
        distance: parseFloat(selectedVehicleData.distance_km),
        duration: Math.round(selectedVehicleData.route_duration_mins),
        toll_charge: parseFloat(selectedVehicleData.toll_price_applied || 0),
        payment_estimate: getEffectiveAmount(discountedEstimate, manualAmountOverride),
        actual_bill: getEffectiveAmount(discountedEstimate, manualAmountOverride),
        eta: Math.round(selectedVehicleData.route_duration_mins), // ETA in minutes
        payment_id: null,
        discount_factor: appliedDiscountFactor,
        source: 'ops',
        source_lead_id: sourceLeadId,
      };

      if (isPartLoadMode && normalizedPartLoadBoxCount !== null) {
        bookingData.box_count = normalizedPartLoadBoxCount;
      }

      console.log('Sending booking data:', bookingData); // Debug logging

      let response;
      if (isModifyMode) {
        if (!bookingIdToModify) {
          throw new Error('Missing booking ID for modification. Please reopen modify booking from the bookings list.');
        }
        // Modify existing booking
        response = await fetch(`${config.customerApiBase}/modify-booking/${bookingIdToModify}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData),
        });
      } else {
        // Create new booking
        response = await fetch(`${config.customerApiBase}/create-booking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `Failed to ${isModifyMode ? 'modify' : 'create'} booking`);
      }

      const data = await response.json();

      // Success - redirect to bookings list or show success message
      alert(`Booking ${isModifyMode ? 'modified' : 'created'} successfully! ${isModifyMode ? `Booking ID: ${bookingIdToModify}` : `Booking ID: ${data.booking_id}`}`);

      // Call the onBookingCreated callback if provided (for compact mode)
      if (onBookingCreated) {
        onBookingCreated(data);
      } else {
        history.push('/app/bookings');
      } // Redirect to bookings page

    } catch (err) {
      setError(`Failed to ${isModifyMode ? 'modify' : 'create'} booking: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedUser || !selectedCustomerId || !pickupAddress) {
      setError('Please select a customer and pickup location to save a draft');
      return;
    }
    if (dropAddresses.every(addr => !addr)) {
      setError('Please select at least one drop location to save a draft');
      return;
    }
    if (selectedVehicle === null) {
      setError('Please select a vehicle to save a draft');
      return;
    }

    if (isFlexBooking) {
      if (!deliveryDeadline) {
        setError('Please select a latest pickup time for the flex booking.');
        return;
      }
      if (deliveryDeadline <= pickupTime) {
        setError('Latest pickup time must be after the earliest pickup time.');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const selectedVehicleData = selectedVehicle !== null ? vehicleOptions[selectedVehicle] : null;
      const priceBreakdown = selectedVehicleData
        ? getPriceBreakdown(selectedVehicleData, bookingDiscountPercent, isB2bCustomer, !!customerGstin)
        : null;
      const appliedDiscountFactor = priceBreakdown?.discountPercentage || 0;
      const discountedEstimate = priceBreakdown?.grandTotal || 0;

      let normalizedRemarks = remarks || '';
      let normalizedPartLoadBoxCount = null;
      if (isPartLoadMode) {
        const parsedBoxCount = parseInt(partLoadBoxCount, 10);
        if (Number.isFinite(parsedBoxCount) && parsedBoxCount >= 1) {
          normalizedPartLoadBoxCount = parsedBoxCount;
          normalizedRemarks = upsertPartLoadBoxRemark(normalizedRemarks, parsedBoxCount);
        }
      }

      const bookingData = {
        customer_id: parseInt(selectedCustomerId),
        pickup_time: formatLocalDateTime(pickupTime),
        pickup_address_id: parseInt(pickupAddress.id),
        drop_address_ids: dropAddresses.filter(Boolean).map(addr => parseInt(addr.id)),
        vehicle_class_id: selectedVehicleData ? parseInt(selectedVehicleData.vehicle_class) : 0,
        remarks: normalizedRemarks,
        material_description: materialDescription.trim() || null,
        delivery_deadline: deliveryDeadline ? formatLocalDateTime(deliveryDeadline) : null,
        booking_type: isPartLoadMode ? 'part' : getBookingType(pickupTime),
        state: 'Draft',
        distance: selectedVehicleData ? parseFloat(selectedVehicleData.distance_km) : 0.0,
        duration: selectedVehicleData ? Math.round(selectedVehicleData.route_duration_mins) : 0,
        toll_charge: selectedVehicleData ? parseFloat(selectedVehicleData.toll_price_applied || 0) : 0.0,
        payment_estimate: getEffectiveAmount(discountedEstimate, manualAmountOverride),
        actual_bill: getEffectiveAmount(discountedEstimate, manualAmountOverride),
        payment_id: null,
        discount_factor: appliedDiscountFactor,
        source: 'ops',
        source_lead_id: sourceLeadId,
      };

      if (isPartLoadMode && normalizedPartLoadBoxCount !== null) {
        bookingData.box_count = normalizedPartLoadBoxCount;
      }

      let response;
      if (isDraftMode && bookingIdToModify) {
        // Update existing draft via update-draft endpoint (designed specifically for drafts)
        response = await fetch(`${config.customerApiBase}/update-draft/${bookingIdToModify}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookingData),
        });
      } else {
        response = await fetch(`${config.customerApiBase}/draft-booking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookingData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to save draft');
      }

      const data = await response.json();
      alert(`Draft ${isDraftMode ? 'updated' : 'saved'} successfully! Draft ID: ${data.booking_id}`);

      if (onBookingCreated) {
        onBookingCreated(data);
      } else {
        history.push('/app/bookings?filter=draft');
      }
    } catch (err) {
      setError(`Failed to save draft: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDraftFromEdit = async () => {
    if (!bookingIdToModify) return;

    // Validate pickup time is not in the past before converting the draft
    const now = new Date();
    if (pickupTime <= now) {
      setError('Pickup time has already passed. Please select a future time before confirming the draft.');
      return;
    }

    if (isFlexBooking) {
      if (!deliveryDeadline) {
        setError('Please select a latest pickup time for the flex booking.');
        return;
      }
      if (deliveryDeadline <= pickupTime) {
        setError('Latest pickup time must be after the earliest pickup time.');
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      // Build booking payload from current form state so confirmation uses latest values
      const selectedVehicleIndex = isPartLoadMode
        ? (selectedVehicle !== null ? selectedVehicle : (vehicleOptions.length > 0 ? 0 : null))
        : selectedVehicle;

      const selectedVehicleData = selectedVehicleIndex !== null ? vehicleOptions[selectedVehicleIndex] : null;
      const priceBreakdown = selectedVehicleData
        ? getPriceBreakdown(selectedVehicleData, bookingDiscountPercent, isB2bCustomer, !!customerGstin)
        : null;
      const appliedDiscountFactor = priceBreakdown?.discountPercentage || 0;
      const discountedEstimate = priceBreakdown?.grandTotal || 0;

      let normalizedRemarks = remarks || '';
      if (isPartLoadMode) {
        const parsedBoxCount = parseInt(partLoadBoxCount, 10);
        if (Number.isFinite(parsedBoxCount) && parsedBoxCount >= 1) {
          normalizedRemarks = upsertPartLoadBoxRemark(normalizedRemarks, parsedBoxCount);
        }
      }

      const bookingData = {
        customer_id: parseInt(selectedCustomerId),
        pickup_time: formatLocalDateTime(pickupTime),
        pickup_address_id: pickupAddress ? parseInt(pickupAddress.id) : null,
        drop_address_ids: dropAddresses.filter(Boolean).map(addr => parseInt(addr.id)),
        vehicle_class_id: selectedVehicleData ? parseInt(selectedVehicleData.vehicle_class) : 0,
        remarks: normalizedRemarks,
        delivery_deadline: deliveryDeadline ? formatLocalDateTime(deliveryDeadline) : null,
        booking_type: isPartLoadMode ? 'part' : getBookingType(pickupTime),
        state: 'Draft',
        distance: selectedVehicleData ? parseFloat(selectedVehicleData.distance_km) : 0.0,
        duration: selectedVehicleData ? Math.round(selectedVehicleData.route_duration_mins) : 0,
        toll_charge: selectedVehicleData ? parseFloat(selectedVehicleData.toll_price_applied || 0) : 0.0,
        payment_estimate: getEffectiveAmount(discountedEstimate, manualAmountOverride),
        actual_bill: getEffectiveAmount(discountedEstimate, manualAmountOverride),
        payment_id: null,
        discount_factor: appliedDiscountFactor,
        source: 'ops',
        source_lead_id: sourceLeadId,
      };


      const response = await fetch(`${config.customerApiBase}/bookings/${bookingIdToModify}/confirm-draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      alert(`Draft confirmed! Booking ID: ${data.booking_id}`);
      history.push('/app/bookings');
    } catch (err) {
      setError(`Failed to confirm draft: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setPickupAddress(null);
    setDropAddresses([null]);
    setPickupMarker(null);
    setDropMarkers([]);
    setActiveMarkerIndex(null);
    setAddressSuggestions([]);
    setRecommendedAddresses([]);
    setRecommendationsLoading(false);
    setError(null);
    setUserSearch('');
    setSelectedUser(null);
    setSelectedCustomerId(null);
    setCustomerDiscountPercent(0);
    setBookingDiscountPercent('');
    setPickupTime(getDefaultPickupTime());
    setVehicleType('');
    setLoadWeight('');
    setLoadLength('');
    setLoadWidth('');
    setLoadHeight('');
    setBodyType('');
    setVehicleOptions([]);
    setSelectedVehicle(null);
    setDeliveryDeadline(null);
    setIsFlexBooking(false);
    setRemarks('');
    setSelectedEquipmentClass(null);
    setEquipmentDuration('');
    setEquipmentPriceEstimate(null);
    setPartLoadBoxCount('1');
  };

  useEffect(() => {
    if (compactMode) return;

    const params = new URLSearchParams(location.search);
    const hasPrefillParams = !!(params.get('user_id') || params.get('customer_id') || params.get('name') || params.get('phone'));
    const hasPrefillProps = !!(prefillUserId || prefillCustomerId || prefillUserName || prefillUserPhone);

    const isNewBookingRouteActive = location.pathname.startsWith('/app/customers/new-booking');
    if (isNewBookingRouteActive && !wasNewBookingRouteActiveRef.current && !hasPrefillParams && !hasPrefillProps) {
      clearForm();
    }
    wasNewBookingRouteActiveRef.current = isNewBookingRouteActive;
  }, [location.pathname, location.search, compactMode, prefillUserId, prefillCustomerId, prefillUserName, prefillUserPhone]);

  // Pre-fill from booking lead (conversion flow)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadId = params.get('sourceLeadId');
    if (!leadId) return;

    setSourceLeadId(parseInt(leadId));

    const customerId = params.get('customerId');
    const customerName = params.get('customerName');
    const customerPhone = params.get('customerPhone');
    const pickupTimeParam = params.get('pickupTime');
    const pickupAddressId = params.get('pickupAddressId');
    const dropAddressIds = params.get('dropAddressIds');
    const vehicleClassId = params.get('vehicleClassId');
    const remarksParam = params.get('remarks');
    const bookingType = params.get('bookingType');

    // Prefill customer
    if (customerId) {
      setSelectedCustomerId(customerId);
      if (customerName || customerPhone) {
        setUserSearch(`${customerName || ''} (${customerPhone || ''})`);
        setSelectedUser({
          id: customerId,
          name: customerName,
          phone_number: customerPhone
        });
      }
    }
    if (pickupTimeParam) {
      try {
        setPickupTime(new Date(pickupTimeParam));
      } catch (e) { /* ignore invalid dates */ }
    }
    if (remarksParam) {
      setRemarks(remarksParam);
    }
    if (bookingType === 'part') {
      setBookingMode('part_load');
    }
    if (vehicleClassId) {
      setVehicleType(vehicleClassId);
    }

    // Resolve pickup address
    if (pickupAddressId) {
      fetch(`${config.customerApiBase}/addresses/${pickupAddressId}`)
        .then(res => res.ok ? res.json() : null)
        .then(addressData => {
          if (addressData) {
            const addressObj = {
              id: addressData.id,
              text: `${addressData.locality || ''}, ${addressData.city || ''}`.trim(),
              lat: addressData.lat,
              lng: addressData.lng,
              full: addressData
            };
            setPickupAddress(addressObj);
            if (addressData.lat && addressData.lng) {
              const markerPos = { lat: addressData.lat, lng: addressData.lng };
              setPickupMarker(markerPos);
              setTimeout(() => {
                if (mapRef.current) {
                  mapRef.current.panTo(markerPos);
                  mapRef.current.setZoom(13);
                }
              }, 500);
            }
          }
        })
        .catch(err => console.error('Error fetching pickup address:', err));
    }

    // Resolve drop addresses
    if (dropAddressIds) {
      const ids = dropAddressIds.split(',').filter(Boolean);
      Promise.all(
        ids.map(addressId =>
          fetch(`${config.customerApiBase}/addresses/${addressId}`)
            .then(res => res.ok ? res.json() : null)
            .then(addressData => {
              if (addressData) {
                return {
                  id: addressData.id,
                  text: `${addressData.locality || ''}, ${addressData.city || ''}`.trim(),
                  lat: addressData.lat,
                  lng: addressData.lng,
                  full: addressData
                };
              }
              return null;
            })
            .catch(() => null)
        )
      ).then(resolved => {
        const valid = resolved.filter(Boolean);
        if (valid.length > 0) {
          setDropAddresses(valid);
          const markers = valid
            .filter(addr => addr.lat && addr.lng)
            .map(addr => ({ lat: addr.lat, lng: addr.lng }));
          setDropMarkers(markers);
        }
      });
    }
  }, [location.search]);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading maps...</div>;
  }

  const hasNonServiceableLocation =
    isNonServiceableAddress(pickupAddress) || dropAddresses.some(isNonServiceableAddress);

  // Gather booking details for the confirmation popup
  const getConfirmationDetails = () => {
    const selectedVehicleIndex = isPartLoadMode
      ? (selectedVehicle !== null ? selectedVehicle : (vehicleOptions.length > 0 ? 0 : null))
      : selectedVehicle;
    const vehicleData = selectedVehicleIndex !== null ? vehicleOptions[selectedVehicleIndex] : null;
    const isHourly = bookingMode === 'hourly';

    let vehicleName = 'Not selected';
    if (isHourly && selectedEquipmentClass) {
      vehicleName = EQUIPMENT_CLASS_MAP[selectedEquipmentClass]?.name || `Class ${selectedEquipmentClass}`;
    } else if (isPartLoadMode) {
      vehicleName = `Part Load (${parseInt(partLoadBoxCount || '1', 10)} boxes)`;
    } else if (vehicleData) {
      vehicleName = vehicleData.display_vehicle_name || vehicleData.vehicle_name || `Class ${vehicleData.vehicle_class}`;
    }

    let price = '—';
    let vehicleDiscountLabel = null;
    if (isHourly && equipmentPriceEstimate) {
      const effective = getEffectiveAmount(
        applyDiscountToAmount(equipmentPriceEstimate.total, bookingDiscountPercent, 0),
        manualAmountOverride
      );
      price = `₹${Number(effective).toLocaleString('en-IN')}`;
    } else if (vehicleData) {
      const breakdown = getPriceBreakdown(vehicleData, bookingDiscountPercent, isB2bCustomer, !!customerGstin);
      if (breakdown) {
        const effective = getEffectiveAmount(breakdown.grandTotal, manualAmountOverride);
        price = `₹${Number(effective).toLocaleString('en-IN')}`;
        if (breakdown.discountPercentage > 0) {
          vehicleDiscountLabel = breakdown.cappedAtBase
            ? `${breakdown.discountPercentage}% (held at base price ₹${Number(breakdown.basePrice).toLocaleString('en-IN')})`
            : `${breakdown.discountPercentage}%`;
        }
      }
    }

    return {
      customer: userSearch || 'Not selected',
      pickupTime: pickupTime ? pickupTime.toLocaleString() : 'Not set',
      bookingType: isPartLoadMode ? 'Part Load' : isHourly ? 'Hourly' : isFlexBooking ? 'Flex' : getBookingType(pickupTime).toUpperCase(),
      pickupAddress: pickupAddress?.text || 'Not set',
      dropAddresses: isHourly ? ['Same as pickup (hourly)'] : dropAddresses.filter(Boolean).map(a => a.text),
      vehicle: vehicleName,
      distance: isHourly ? '—' : (vehicleData ? `${vehicleData.distance_km} km` : '—'),
      duration: isHourly ? (equipmentDuration ? `${equipmentDuration}h` : '—') : (vehicleData ? `${Math.round(vehicleData.route_duration_mins)} min` : '—'),
      price,
      toll: vehicleData && Number(vehicleData.toll_price_applied) > 0 ? `₹${Number(vehicleData.toll_price_applied).toFixed(2)}` : null,
      discount: isHourly
        ? (normalizeDiscountPercent(bookingDiscountPercent) > 0 ? `${bookingDiscountPercent}%` : null)
        : vehicleDiscountLabel,
      flexWindow: isFlexBooking && deliveryDeadline ? `${pickupTime.toLocaleString()} — ${deliveryDeadline.toLocaleString()}` : null,
      remarks: remarks || null,
      material: materialDescription?.trim() || null,
      partLoadBoxes: isPartLoadMode ? parseInt(partLoadBoxCount || '1', 10) : null,
      bookingId: bookingIdToModify || null,
    };
  };

  const handleShowConfirmPopup = (type) => {
    setConfirmPopupType(type);
    setShowConfirmPopup(true);
  };

  const handlePopupConfirm = () => {
    setShowConfirmPopup(false);
    switch (confirmPopupType) {
      case 'confirm':
        handleConfirmBooking();
        break;
      case 'modify':
        handleConfirmBooking();
        break;
      case 'confirm_draft':
        handleConfirmDraftFromEdit();
        break;
      case 'confirm_hourly':
        handleConfirmHourlyBooking();
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      {/* Header */}
      {!compactMode && (
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <div className={styles.titleIcon}>
              <i className={`fa ${isModifyMode ? 'fa-edit' : 'fa-plus-circle'}`} />
            </div>
            <h2>{isModifyMode ? 'Modify Booking' : 'New Booking'}</h2>
            {isModifyMode && modifyBookingData && (
              <span className={styles.bookingRef}>Booking ID: #{modifyBookingData.id}</span>
            )}
          </div>
          <div className={styles.headerActions}>
            <button className={styles.backBtn} onClick={() => history.push(isModifyMode ? '/app/bookings' : '/app/customers')}>
              <i className="fa fa-arrow-left" /> Back to {isModifyMode ? 'Bookings' : 'Customers'}
            </button>
          </div>
        </div>
      )}

      {/* Booking Mode Tabs */}
      {!isModifyMode && !compactMode && (
        <div className={styles.bookingModeTabs}>
          <button
            className={`${styles.modeTab} ${bookingMode === 'standard' ? styles.modeTabActive : ''}`}
            onClick={() => setBookingMode('standard')}
          >
            <i className="fa fa-truck" />
            Standard Booking
            <span className={styles.modeTabDesc}>A to B transport</span>
          </button>
          <button
            className={`${styles.modeTab} ${bookingMode === 'hourly' ? styles.modeTabActive : ''}`}
            onClick={() => setBookingMode('hourly')}
          >
            <i className="fa fa-clock-o" />
            Hourly Booking
            <span className={styles.modeTabDesc}>Equipment by the hour</span>
          </button>
          <button
            className={`${styles.modeTab} ${bookingMode === 'part_load' ? styles.modeTabActive : ''}`}
            onClick={() => setBookingMode('part_load')}
          >
            <i className="fa fa-cubes" />
            Part Load
            <span className={styles.modeTabDesc}>Shared load by boxes</span>
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className={`${styles.alert} ${styles.alertDanger}`}>
          <i className="fa fa-exclamation-circle" style={{ marginRight: '0.5rem' }} />
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#c92a2a' }}
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}

      {hasNonServiceableLocation && (
        <div className={styles.serviceAreaNotice}>
          <i className="fa fa-info-circle" />
          <span>
            One or more locations are outside the standard service area. Ops bookings can still proceed — verify pricing before confirming.
          </span>
        </div>
      )}

      {(bookingMode === 'standard' || bookingMode === 'part_load' || isModifyMode) && (
      <div className={styles.contentGrid}>
        {/* Left Column - Form */}
        <div>
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <i className="fa fa-user" /> Customer & Schedule
            </div>
            <div className={styles.cardBody}>
              {/* User Search */}
              <div className={styles.formGroup}>
                <label>User</label>
                <div className={styles.inputWrapper}>
                  <Input
                    type="text"
                    placeholder="Search by name or phone number"
                    value={userSearch}
                    onChange={(e) => handleUserSearch(e.target.value)}
                    disabled={!!selectedUser}
                  />
                  {selectedUser && (
                    <button className={styles.clearBtn} onClick={clearSelectedUser}>
                      <i className="fa fa-times" />
                    </button>
                  )}
                </div>
                {userSuggestions.length > 0 && (
                  <div className={styles.suggestionsList}>
                    {userSuggestions.map((user) => (
                      <button
                        key={user.id}
                        className={styles.suggestionItem}
                        onClick={() => handleUserSelect(user)}
                      >
                        {user.name} ({user.phone}){user.is_b2b ? ' · B2B' : ''}
                      </button>
                    ))}
                  </div>
                )}
                {selectedCustomerId && isB2bCustomer && (
                  <div className={styles.helpText}>
                    B2B account — {customerDiscountPercent > 0
                      ? `${customerDiscountPercent}% discount (excl. toll) + 5% GST on subtotal`
                      : '5% GST on subtotal'}
                  </div>
                )}
              </div>

              {/* Pickup Time */}
              <div className={styles.formGroup}>
                <label>Pickup Time</label>
                <div className={styles.inputWrapper}>
                  <DatePicker
                    selected={pickupTime}
                    onChange={(date) => setPickupTime(date)}
                    showTimeSelect
                    dateFormat="Pp"
                    className="form-control"
                    minDate={new Date()}
                  />
                  <span className={`${styles.bookingTypeBadge} ${getBookingTypeBadgeClass()}`}>
                    {isPartLoadMode ? 'part load' : getBookingType(pickupTime)}
                  </span>
                </div>
                <div className={styles.helpText}>
                  {isPartLoadMode
                    ? 'Part-load booking. Pricing depends on route and number of boxes.'
                    : (getBookingType(pickupTime) === 'instant'
                      ? 'Within 2 hours - Instant booking'
                      : 'More than 2 hours away - Scheduled booking')
                  }
                </div>
                {!isPartLoadMode && !isModifyMode && bookingMode === 'standard' && (
                  <div className={styles.flexToggleRow}>
                    <label className={styles.flexToggle}>
                      <input
                        type="checkbox"
                        checked={isFlexBooking}
                        onChange={(e) => setIsFlexBooking(e.target.checked)}
                      />
                      <span className={styles.flexToggleLabel}>
                        <i className="fa fa-clock-o" /> Flex booking (25% off)
                      </span>
                      <span className={styles.flexToggleSlider} />
                    </label>
                    <div className={styles.helpText}>
                      {isFlexBooking
                        ? 'We will pick up anytime between the earliest and latest time you set.'
                        : 'Enable if you can keep the pickup time flexible and get 25% off.'}
                    </div>
                  </div>
                )}
                {isModifyMode && (
                  <div className={styles.modifyWarning}>
                    <i className="fa fa-exclamation-triangle" />
                    <strong>Important:</strong> When modifying a booking, the pickup time must be at least 20 minutes in the future. If the current pickup time is close, please adjust it accordingly.
                  </div>
                )}
              </div>

              {/* Pickup Location */}
              <div className={styles.formGroup}>
                <label>Pickup Location</label>
                <div className={styles.inputWrapper}>
                  <Input
                    type="text"
                    placeholder="Enter pickup location or click map"
                    value={pickupAddress?.text || ''}
                    onChange={(e) => setPickupAddress({ ...pickupAddress, text: e.target.value })}
                    onFocus={() => setActiveMarkerIndex(-1)}
                  />
                  <button
                    className={styles.searchBtn}
                    onClick={() => handleSearchLocation(true)}
                    disabled={!pickupAddress?.text}
                  >
                    Search
                  </button>
                </div>
                {!isModifyMode && selectedCustomerId && (
                  <div className={styles.recommendationsBlock}>
                    <div className={styles.recommendationsLabel}>Saved addresses (quick pick)</div>
                    {recommendationsLoading ? (
                      <div className={styles.helpText}>Loading saved addresses...</div>
                    ) : (
                      <div className={styles.recommendationsChips}>
                        {recommendedAddresses.length > 0 ? (
                          recommendedAddresses.map((address) => (
                            <button
                              key={`pickup-rec-${address.id}`}
                              type="button"
                              className={styles.recommendationChip}
                              onClick={() => applyRecommendedAddress(address, true)}
                            >
                              <div className={styles.recommendationTopRow}>
                                <span className={styles.recommendationTitle}>{getRecommendedAddressTitle(address)}</span>
                                {address.is_primary && (
                                  <span className={styles.primaryBadge}>Home</span>
                                )}
                              </div>
                              <div className={styles.recommendationMeta}>
                                <i className="fa fa-map-marker" />
                                <span>{getRecommendedAddressSubtitle(address) || 'Saved customer address'}</span>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className={styles.helpText}>No saved addresses found for this customer.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {pickupAddress && !addressSuggestions.length && (
                  <div className={styles.helpText}>Click on map or use search to update location</div>
                )}
                {/* Inline Address Suggestions for Pickup */}
                {addressSuggestions.length > 0 && activeMarkerIndex === -1 && (
                  <div className={styles.inlineSuggestionsCard}>
                    <div className={styles.suggestionsHeader}>
                      <i className="fa fa-map-marker" />
                      <span>Found {addressSuggestions.length} similar addresses - please select one:</span>
                    </div>
                    {addressSuggestions.map((suggestion, idx) => (
                      <div
                        key={suggestion.id || `${suggestion.latitude}-${suggestion.longitude}-${idx}`}
                        className={styles.suggestionItem}
                        onClick={() => applyAddressSuggestion(suggestion, true)}
                      >
                        <strong>{suggestion.locality}</strong>
                        {isNonServiceableAddress(suggestion) && (
                          <span className={styles.nonServiceableBadge}>Outside service area</span>
                        )}
                        <br />
                        <small style={{ color: '#6c757d' }}>
                          {suggestion.city}, {suggestion.state} - {suggestion.pin_code}
                          <span className={styles.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>
                        </small>
                      </div>
                    ))}
                    <button className={styles.closeBtn} onClick={() => { setAddressSuggestions([]); setError(null); }}>
                      Close Suggestions
                    </button>
                  </div>
                )}
                {pickupAddress && isNonServiceableAddress(pickupAddress) && (
                  <div className={styles.helpText}>Pickup is outside the standard service area.</div>
                )}
              </div>

              {/* Drop Locations */}
              {dropAddresses.map((addr, index) => (
                <div className={styles.formGroup} key={index}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ margin: 0 }}>Drop Location {index + 1}</label>
                    {dropAddresses.length > 1 && (
                      <button className={styles.removeDropBtn} onClick={() => removeDropLocation(index)}>
                        <i className="fa fa-times" /> Remove
                      </button>
                    )}
                  </div>
                  <div className={styles.inputWrapper}>
                    <Input
                      type="text"
                      placeholder="Enter drop location or click map"
                      value={addr?.text || ''}
                      onChange={(e) => {
                        const newDropAddresses = [...dropAddresses];
                        newDropAddresses[index] = { ...addr, text: e.target.value };
                        setDropAddresses(newDropAddresses);
                      }}
                      onFocus={() => setActiveMarkerIndex(index)}
                    />
                    <button
                      className={styles.searchBtn}
                      onClick={() => handleSearchLocation(false, index)}
                      disabled={!addr?.text}
                    >
                      Search
                    </button>
                  </div>
                  {!isModifyMode && selectedCustomerId && (
                    <div className={styles.recommendationsBlock}>
                      <div className={styles.recommendationsLabel}>Saved addresses (quick pick)</div>
                      {recommendationsLoading ? (
                        <div className={styles.helpText}>Loading saved addresses...</div>
                      ) : (
                        <div className={styles.recommendationsChips}>
                          {recommendedAddresses
                            .filter((address) => String(address.id) !== String(pickupAddress?.id))
                            .map((address) => (
                              <button
                                key={`drop-${index}-rec-${address.id}`}
                                type="button"
                                className={styles.recommendationChip}
                                onClick={() => applyRecommendedAddress(address, false, index)}
                              >
                                <div className={styles.recommendationTopRow}>
                                  <span className={styles.recommendationTitle}>{getRecommendedAddressTitle(address)}</span>
                                  {address.is_primary && (
                                    <span className={styles.primaryBadge}>Home</span>
                                  )}
                                </div>
                                <div className={styles.recommendationMeta}>
                                  <i className="fa fa-map-marker" />
                                  <span>{getRecommendedAddressSubtitle(address) || 'Saved customer address'}</span>
                                </div>
                              </button>
                            ))}
                          {recommendedAddresses.filter((address) => String(address.id) !== String(pickupAddress?.id)).length === 0 && (
                            <div className={styles.helpText}>No additional saved addresses for drop.</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {addr && !addressSuggestions.length && (
                    <div className={styles.helpText}>Click on map or use search to update location</div>
                  )}
                  {/* Inline Address Suggestions for this Drop Location */}
                  {addressSuggestions.length > 0 && activeMarkerIndex === index && (
                    <div className={styles.inlineSuggestionsCard}>
                      <div className={styles.suggestionsHeader}>
                        <i className="fa fa-map-marker" />
                        <span>Found {addressSuggestions.length} similar addresses - please select one:</span>
                      </div>
                      {addressSuggestions.map((suggestion, idx) => (
                        <div
                          key={suggestion.id || `${suggestion.latitude}-${suggestion.longitude}-${idx}`}
                          className={styles.suggestionItem}
                          onClick={() => applyAddressSuggestion(suggestion, false, index)}
                        >
                          <strong>{suggestion.locality}</strong>
                          {isNonServiceableAddress(suggestion) && (
                            <span className={styles.nonServiceableBadge}>Outside service area</span>
                          )}
                          <br />
                          <small style={{ color: '#6c757d' }}>
                            {suggestion.city}, {suggestion.state} - {suggestion.pin_code}
                            <span className={styles.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>
                          </small>
                        </div>
                      ))}
                      <button className={styles.closeBtn} onClick={() => { setAddressSuggestions([]); setError(null); }}>
                        Close Suggestions
                      </button>
                    </div>
                  )}
                  {addr && isNonServiceableAddress(addr) && (
                    <div className={styles.helpText}>Drop is outside the standard service area.</div>
                  )}
                </div>
              ))}

              <button className={styles.addDropBtn} onClick={addDropLocation} type="button">
                <i className="fa fa-plus" /> Add Drop Location
              </button>
            </div>
          </div>

          {/* Vehicle Requirements Card */}
          <div className={styles.formCard} style={{ marginTop: '1.5rem' }}>
            <div className={`${styles.cardHeader} ${isPartLoadMode ? styles.partLoadHeader : styles.infoHeader}`}>
              <i className={`fa ${isPartLoadMode ? 'fa-cubes' : 'fa-truck'}`} /> {isPartLoadMode ? 'Part Load Details' : 'Vehicle Requirements'}
            </div>
            <div className={styles.cardBody}>
              {isPartLoadMode ? (
                <>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Number of Boxes</label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="e.g., 3"
                        value={partLoadBoxCount}
                        onChange={(e) => setPartLoadBoxCount(e.target.value)}
                      />
                      <div className={styles.helpText}>Part-load pricing is based on route and box count.</div>
                    </div>
                    <div className={styles.formGroup}>
                      <label>Vehicle Selection</label>
                      <Input type="text" value="Auto-selected (Part Load)" disabled />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      className={styles.vehicleOptionsBtn}
                      onClick={() => handleGetVehicleOptions(false)}
                      disabled={loading || !selectedUser || !pickupAddress || dropAddresses.some(addr => !addr)}
                    >
                      {loading ? (
                        <><i className="fa fa-spinner fa-spin" /> Getting Part-Load Pricing...</>
                      ) : (
                        <><i className="fa fa-cubes" /> Get Part-Load Pricing</>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Vehicle Type (Optional)</label>
                      <Input
                        type="text"
                        placeholder="e.g., Tata Ace, Mahindra Bolero"
                        value={vehicleType}
                        onChange={(e) => setVehicleType(e.target.value)}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Body Type (Optional)</label>
                      <Input
                        type="select"
                        value={bodyType}
                        onChange={(e) => setBodyType(e.target.value)}
                      >
                        <option value="">Select body type</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                        <option value="container">Container</option>
                        <option value="flatbed">Flatbed</option>
                        <option value="tanker">Tanker</option>
                      </Input>
                    </div>
                  </div>

                  <div className={`${styles.formRow} ${styles.fourCols}`}>
                    <div className={styles.formGroup}>
                      <label>Load Weight (tons)</label>
                      <Input
                        type="number"
                        placeholder="e.g., 2.5"
                        value={loadWeight}
                        onChange={(e) => setLoadWeight(e.target.value)}
                        step="0.1"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Length (ft)</label>
                      <Input
                        type="number"
                        placeholder="e.g., 10"
                        value={loadLength}
                        onChange={(e) => setLoadLength(e.target.value)}
                        step="0.1"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Width (ft)</label>
                      <Input
                        type="number"
                        placeholder="e.g., 6"
                        value={loadWidth}
                        onChange={(e) => setLoadWidth(e.target.value)}
                        step="0.1"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Height (ft)</label>
                      <Input
                        type="number"
                        placeholder="e.g., 4"
                        value={loadHeight}
                        onChange={(e) => setLoadHeight(e.target.value)}
                        step="0.1"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      className={styles.vehicleOptionsBtn}
                      onClick={() => handleGetVehicleOptions('vehicle')}
                      disabled={loading || !selectedUser || !pickupAddress || dropAddresses.some(addr => !addr)}
                    >
                      {loading ? (
                        <><i className="fa fa-spinner fa-spin" /> Getting Vehicle Options...</>
                      ) : (
                        <><i className="fa fa-truck" /> Get Vehicle Options & Pricing</>
                      )}
                    </button>

                    <button
                      className={styles.vehicleOptionsBtn}
                      onClick={() => handleGetVehicleOptions('allClasses')}
                      disabled={loading || !selectedUser || !pickupAddress || dropAddresses.some(addr => !addr)}
                    >
                      {loading ? (
                        <><i className="fa fa-spinner fa-spin" /> Getting All Class Rates...</>
                      ) : (
                        <><i className="fa fa-list" /> Get Rates for All Classes</>
                      )}
                    </button>

                    <button
                      className={styles.vehicleOptionsBtn}
                      onClick={() => handleGetVehicleOptions('tipper')}
                      disabled={loading || !selectedUser || !pickupAddress || dropAddresses.some(addr => !addr)}
                      style={{ backgroundColor: '#e67e22' }}
                    >
                      {loading ? (
                        <><i className="fa fa-spinner fa-spin" /> Getting Tipper Rates...</>
                      ) : (
                        <><i className="fa fa-arrow-up" /> Get Tipper Rates</>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Vehicle Options Display */}
              {vehicleOptions.length > 0 && (
                <div className={styles.vehicleOptionsSection}>
                  <h5>{isPartLoadMode ? 'Part-Load Pricing' : 'Available Vehicle Options'}</h5>

                  {!isPartLoadMode && vehicleOptions[0] && (
                    <div className={styles.routeSummary}>
                      <span><i className="fa fa-road" /> {vehicleOptions[0].distance_km} km</span>
                      <span><i className="fa fa-clock-o" /> {Math.round(vehicleOptions[0].route_duration_mins)} min</span>
                    </div>
                  )}

                  {isPartLoadMode ? (
                    <div className={styles.vehicleGrid}>
                      {vehicleOptions.slice(0, 1).map((vehicle, index) => {
                        const currentPct = normalizeDiscountPercent(bookingDiscountPercent);
                        const originalPrice = computeOriginalPrice(vehicle, isB2bCustomer, !!customerGstin);
                        const flexPrice = computeFlexPrice(vehicle, isB2bCustomer, !!customerGstin);
                        return (
                        <div
                          key={index}
                          className={`${styles.vehicleCard} ${styles.selected}`}
                          onClick={() => setSelectedVehicle(index)}
                        >
                          <div className={styles.vehicleHeader}>
                            <h6 className={styles.vehicleName}>Part Load ({parseInt(partLoadBoxCount || '1', 10)} boxes)</h6>
                            <span className={styles.vehiclePrice}>
                              ₹{formatVehicleListPrice(vehicle, bookingDiscountPercent, isB2bCustomer, !!customerGstin)}
                            </span>
                          </div>
                          <div className={styles.vehicleSpecs}>
                            Route {vehicle.distance_km} km • Approx. {Math.round(vehicle.route_duration_mins)} min
                          </div>
                          <div className={styles.vehicleStats}>
                            <div className={styles.stat}>
                              <strong>{parseInt(partLoadBoxCount || '1', 10)}</strong>
                              <span>Boxes</span>
                            </div>
                            <div className={styles.stat}>
                              <strong>₹{vehicle.unit_price_per_km_per_box ?? vehicle.unit_price_per_km}</strong>
                              <span>Rate / km / box</span>
                            </div>
                            <div className={styles.stat}>
                              <strong>₹{vehicle.toll_price_applied}</strong>
                              <span>Tolls</span>
                            </div>
                          </div>
                          {(() => {
                            const cardBreakdown = getPriceBreakdown(vehicle, currentPct, isB2bCustomer, !!customerGstin);
                            if (!cardBreakdown || cardBreakdown.discountAmount <= 0) return null;
                            return (
                              <div className={styles.discountIndicator}>
                                <i className="fa fa-tag" /> {cardBreakdown.cappedAtBase
                                  ? `Discount held at base price (−₹${cardBreakdown.discountAmount.toLocaleString('en-IN')})`
                                  : `${currentPct}% discount applied`}
                                <span className={styles.originalPriceStrikethrough}>₹{originalPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                              </div>
                            );
                          })()}
                          <div className={styles.flexPriceRow}>
                            <i className="fa fa-clock-o" /> {getPriceBreakdown(vehicle, FLEX_DISCOUNT_PERCENT, isB2bCustomer, !!customerGstin)?.cappedAtBase ? 'Flex:' : '25% Flex:'}
                            <span className={styles.flexPriceValue}>₹{flexPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <div className={styles.selectedBadge}>
                            <i className="fa fa-check-circle" /> Auto-selected for Part Load
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.vehicleGrid}>
                      {vehicleOptions.map((vehicle, index) => {
                        const currentPct = normalizeDiscountPercent(bookingDiscountPercent);
                        const originalPrice = computeOriginalPrice(vehicle, isB2bCustomer, !!customerGstin);
                        const cardQuote = getPriceBreakdown(vehicle, currentPct, isB2bCustomer, !!customerGstin);
                        const flexQuote = getPriceBreakdown(vehicle, FLEX_DISCOUNT_PERCENT, isB2bCustomer, !!customerGstin);
                        const flexPrice = flexQuote?.grandTotal || 0;
                        return (
                        <div
                          key={index}
                          className={`${styles.vehicleCard} ${selectedVehicle === index ? styles.selected : ''}`}
                          onClick={() => setSelectedVehicle(index)}
                        >
                          <div className={styles.vehicleHeader}>
                            <h6 className={styles.vehicleName}>{vehicle.display_vehicle_name || vehicle.vehicle_name}</h6>
                            <span className={styles.vehiclePrice}>
                              ₹{formatVehicleListPrice(vehicle, bookingDiscountPercent, isB2bCustomer, !!customerGstin)}
                              {(isB2bCustomer || customerGstin) && (
                                <small style={{ display: 'block', fontSize: '10px', fontWeight: 500, opacity: 0.85 }}>
                                  incl. {cardQuote?.discountAmount > 0 ? `${cardQuote.cappedAtBase ? 'base-price' : `${currentPct}%`} disc + ` : ''}GST
                                </small>
                              )}
                            </span>
                          </div>
                          <div className={styles.vehicleSpecs}>
                            Class {vehicle.vehicle_class} • {vehicle.payload_tons} tons • {vehicle.length_ft} ft
                          </div>
                          <div className={styles.vehicleStats}>
                            <div className={styles.stat}>
                              <strong>₹{vehicle.toll_price_applied}</strong>
                              <span>Tolls</span>
                            </div>
                          </div>
                          {cardQuote?.discountAmount > 0 && (
                            <div className={styles.discountIndicator}>
                              <i className="fa fa-tag" /> {cardQuote.cappedAtBase
                                ? `Discount held at base price (−₹${cardQuote.discountAmount.toLocaleString('en-IN')})`
                                : `${currentPct}% discount applied`}
                              <span className={styles.originalPriceStrikethrough}>₹{originalPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                          )}
                          <div className={styles.flexPriceRow}>
                            <i className="fa fa-clock-o" /> {flexQuote?.cappedAtBase ? 'Flex:' : '25% Flex:'}
                            <span className={styles.flexPriceValue}>₹{flexPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          {selectedVehicle === index && (
                            <div className={styles.selectedBadge}>
                              <i className="fa fa-check-circle" /> Selected
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Additional Details Card */}
          <div className={styles.formCard} style={{ marginTop: '1.5rem' }}>
            <div className={`${styles.cardHeader} ${styles.successHeader}`}>
              <i className="fa fa-clipboard" /> Additional Details
            </div>
            <div className={styles.cardBody}>
              <div className={styles.formGroup}>
                <label>{isFlexBooking ? 'Latest Pickup Time *' : 'Delivery Deadline (Optional)'}</label>
                <DatePicker
                  selected={deliveryDeadline}
                  onChange={(date) => setDeliveryDeadline(date)}
                  showTimeSelect
                  dateFormat="Pp"
                  minDate={pickupTime}
                  placeholderText={isFlexBooking ? "Select latest pickup time..." : "Select delivery deadline..."}
                  className="form-control"
                  required={isFlexBooking}
                />
                <div className={styles.helpText}>
                  {isFlexBooking
                    ? 'The vehicle will arrive at any time between the earliest and latest pickup time.'
                    : 'When do you need this delivery completed?'}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Remarks / Special Instructions</label>
                <Input
                  type="textarea"
                  rows="3"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Any special instructions, notes, or requirements for this booking..."
                />
              </div>

              <div className={styles.formGroup}>
                <label>Materials / Load Description</label>
                <Input
                  type="text"
                  value={materialDescription}
                  onChange={(e) => setMaterialDescription(e.target.value)}
                  placeholder="e.g. Cement bags, Furniture items, Steel rods (approx 2 tons)"
                />
                <div className={styles.helpText}>
                  What materials will be transported? This helps the driver prepare.
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Discount (%)</label>
                <div className={styles.inputWrapper}>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    value={bookingDiscountPercent}
                    onChange={(e) => setBookingDiscountPercent(e.target.value)}
                    disabled={isFlexBooking}
                  />
                </div>
                <div className={styles.helpText}>
                  {isFlexBooking ? (
                    <>
                      <i className="fa fa-clock-o" /> Flex booking discount applied:
                      <strong>{normalizeDiscountPercent(bookingDiscountPercent)}%</strong> off base fare (excl. toll).
                      {customerDiscountPercent > 0 && customerDiscountPercent > 25 && (
                        <span> Your account discount ({customerDiscountPercent}%) is higher, so it is used instead.</span>
                      )}
                    </>
                  ) : (
                    <>
                      {isB2bCustomer && 'B2B customer — '}
                      {customerGstin && !isB2bCustomer && `GSTIN: ${customerGstin} — `}
                      {customerDiscountPercent > 0
                        ? `Account discount: ${customerDiscountPercent}% (excl. toll). ${(isB2bCustomer || customerGstin) ? '5% GST added on subtotal.' : ''}`
                        : customerBookingCount === 0
                          ? `First booking welcome discount: ${FIRST_BOOKING_DISCOUNT_PERCENT}% off base fare (excl. toll).`
                          : (isB2bCustomer || customerGstin)
                            ? 'No account discount set. 5% GST will be added on subtotal.'
                            : 'No preloaded customer discount. You can still enter a manual discount for this booking.'}
                    </>
                  )}
                </div>
              </div>

              <div className={styles.actionButtons}>
                {isDraftMode && (
                  <button
                    className={styles.confirmBtn}
                    onClick={() => handleShowConfirmPopup('confirm_draft')}
                    disabled={loading || !selectedUser || !pickupAddress || dropAddresses.some(addr => !addr) || (isPartLoadMode ? vehicleOptions.length === 0 : selectedVehicle === null)}
                  >
                    {loading ? (
                      <><i className="fa fa-spinner fa-spin" /> Confirming Draft...</>
                    ) : (
                      <><i className="fa fa-check-circle" /> Confirm Booking</>
                    )}
                  </button>
                )}
                <button
                  className={isDraftMode ? styles.saveDraftBtn : styles.confirmBtn}
                  onClick={isDraftMode ? handleSaveDraft : () => handleShowConfirmPopup(isModifyMode ? 'modify' : 'confirm')}
                  disabled={loading || !selectedUser || !pickupAddress || dropAddresses.every(addr => !addr) || selectedVehicle === null}
                >
                  {loading ? (
                    <><i className="fa fa-spinner fa-spin" /> {isDraftMode ? 'Updating Draft...' : (isModifyMode ? 'Modifying Booking...' : 'Creating Booking...')}</>
                  ) : (
                    <>
                      <i className={`fa ${isDraftMode ? 'fa-file-text-o' : (isModifyMode ? 'fa-edit' : 'fa-check-circle')}`} />
                      {isDraftMode ? 'Update Draft' : (isModifyMode ? 'Modify Booking' : (isPartLoadMode ? 'Confirm Part-Load Booking' : 'Confirm Booking'))}
                    </>
                  )}
                </button>
                {!isDraftMode && (
                  <button
                    className={styles.saveDraftBtn}
                    onClick={handleSaveDraft}
                    disabled={loading || !selectedUser || !pickupAddress || dropAddresses.every(addr => !addr) || selectedVehicle === null}
                  >
                    <i className="fa fa-file-text-o" /> Save as Draft
                  </button>
                )}
                <button className={styles.cancelBtn} onClick={() => history.push(isModifyMode ? '/app/bookings' : '/app/customers')}>
                  <i className="fa fa-arrow-left" /> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Map & Summary (Sticky) */}
        <div className={styles.rightColumn}>
          <div className={styles.stickyContainer}>
            <div className={styles.mapCard}>
              <div className={styles.mapHeader}>
                <i className="fa fa-map-marker" /> Interactive Location Map
              </div>
              <div className={styles.mapLegend}>
                <div className={styles.legendItems}>
                  <div className={styles.legendItem}>
                    <span className={`${styles.dot} ${styles.pickup}`}></span>
                    Pickup
                  </div>
                  <div className={styles.legendItem}>
                    <span className={`${styles.dot} ${styles.drop}`}></span>
                    Drop
                  </div>
                </div>
                <span className={`${styles.activeBadge} ${activeMarkerIndex === -1 ? styles.pickup : activeMarkerIndex !== null ? styles.drop : styles.none}`}>
                  {activeMarkerIndex === -1 ? 'Setting Pickup' :
                    activeMarkerIndex !== null ? `Setting Drop ${activeMarkerIndex + 1}` :
                      'Select location field first'}
                </span>
              </div>
              <div className={styles.mapContainer}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={12}
                  center={defaultCenter}
                  onLoad={onMapLoad}
                  onClick={handleMapClick}
                  options={{
                    styles: mapStyles,
                    disableDefaultUI: false,
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: true,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: true
                  }}
                >
                  {!pickupMarker && dropMarkers.length === 0 && (
                    <div className={styles.mapInstructions}>
                      <i className="fa fa-info-circle" />
                      <span>Select a location field, then click the map</span>
                    </div>
                  )}
                  {pickupMarker && (
                    <Marker
                      position={pickupMarker}
                      icon={{
                        url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
                        scaledSize: new window.google.maps.Size(40, 40)
                      }}
                      title="Pickup Location"
                      animation={window.google.maps.Animation.DROP}
                    />
                  )}
                  {dropMarkers.map((marker, index) =>
                    marker && (
                      <Marker
                        key={index}
                        position={marker}
                        icon={{
                          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                          scaledSize: new window.google.maps.Size(40, 40)
                        }}
                        title={`Drop Location ${index + 1}`}
                        animation={window.google.maps.Animation.DROP}
                      />
                    )
                  )}
                </GoogleMap>
              </div>
            </div>

            {/* Booking Summary - Inside sticky container */}
            {(pickupAddress || dropAddresses.some(addr => addr) || selectedVehicle !== null) && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryHeader} ${isPartLoadMode ? styles.partLoadSummaryHeader : ''}`}>
                  <i className="fa fa-clipboard" /> Booking Summary
                </div>
                <div className={styles.summaryBody}>
                  {selectedUser && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>User:</span>
                      <span className={styles.summaryValue}>{userSearch}</span>
                    </div>
                  )}
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Pickup Time:</span>
                    <span className={styles.summaryValue}>{pickupTime.toLocaleString()}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Booking Type:</span>
                    <span className={styles.summaryValue}>
                      <span className={`${styles.bookingTypeBadge} ${getBookingTypeBadgeClass()}`}>
                        {isPartLoadMode ? 'PART LOAD' : getBookingType(pickupTime).toUpperCase()}
                      </span>
                    </span>
                  </div>
                  {isFlexBooking && deliveryDeadline && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Flex Window:</span>
                      <span className={styles.summaryValue}>
                        {pickupTime.toLocaleString()} — {deliveryDeadline.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {pickupAddress && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Pickup:</span>
                      <span className={styles.summaryValue}>{pickupAddress.text}</span>
                    </div>
                  )}
                  {dropAddresses.map((addr, index) =>
                    addr && (
                      <div key={index} className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Drop {index + 1}:</span>
                        <span className={styles.summaryValue}>{addr.text}</span>
                      </div>
                    )
                  )}
                  {selectedVehicle !== null && vehicleOptions[selectedVehicle] && (
                    <div className={styles.selectedVehicleSummary}>
                      <div className={styles.vehicleInfo}>
                        <div className={styles.vehicleName}>
                          {isPartLoadMode
                            ? `Part Load (${parseInt(partLoadBoxCount || '1', 10)} boxes)`
                            : `Selected: ${vehicleOptions[selectedVehicle].display_vehicle_name || vehicleOptions[selectedVehicle].vehicle_name}`}
                        </div>
                        <div className={styles.vehicleDetails}>
                          {vehicleOptions[selectedVehicle].distance_km} km •
                          {Math.round(vehicleOptions[selectedVehicle].route_duration_mins)} min •
                          {isPartLoadMode ? 'Part-load pricing' : `Class ${vehicleOptions[selectedVehicle].vehicle_class}`}
                        </div>
                      </div>
                       <div className={styles.priceInfo}>
                        {(() => {
                          const breakdown = getPriceBreakdown(
                            vehicleOptions[selectedVehicle],
                            bookingDiscountPercent,
                            isB2bCustomer,
                            !!customerGstin
                          );
                          if (!breakdown) return null;
                          const effectiveAmount = getEffectiveAmount(breakdown.grandTotal, manualAmountOverride);
                          const isOverridden = manualAmountOverride !== '' && parseFloat(manualAmountOverride) !== breakdown.grandTotal;
                          const flexPrice = computeFlexPrice(vehicleOptions[selectedVehicle], isB2bCustomer, !!customerGstin);
                          const currentPct = normalizeDiscountPercent(bookingDiscountPercent);
                          return (
                            <>
                              {(isB2bCustomer || customerGstin) && (
                                <div className={styles.priceLabel}>Incl. 5% GST on subtotal</div>
                              )}
                              {breakdown.discountAmount > 0 && (
                                <div className={styles.priceLabel}>
                                  {breakdown.cappedAtBase
                                    ? `Discount held at base price ₹${Number(breakdown.basePrice).toLocaleString('en-IN')}: −₹${breakdown.discountAmount.toLocaleString()}`
                                    : `${breakdown.discountPercentage}% discount (excl. toll): −₹${breakdown.discountAmount.toLocaleString()}`}
                                </div>
                              )}
                              {(isB2bCustomer || customerGstin) && breakdown.gstAmount > 0 && (
                                <div className={styles.priceLabel}>GST (5%): ₹{breakdown.gstAmount.toLocaleString()}</div>
                              )}
                              <div className={styles.editablePriceWrapper}>
                                <span className={styles.currencySymbol}>₹</span>
                                <input
                                  type="number"
                                  className={`${styles.editablePriceInput} ${isOverridden ? styles.overridden : ''}`}
                                  value={manualAmountOverride !== '' ? manualAmountOverride : ''}
                                  onChange={(e) => setManualAmountOverride(e.target.value)}
                                  onFocus={(e) => {
                                    if (manualAmountOverride === '') {
                                      e.target.value = '';
                                    }
                                  }}
                                  placeholder={breakdown.grandTotal.toLocaleString()}
                                  title="Click to modify the final amount"
                                />
                              </div>
                              {isOverridden && (
                                <div className={`${styles.priceLabel} ${styles.overriddenLabel}`}>
                                  <i className="fa fa-pencil" style={{ marginRight: '4px' }} />
                                  Overridden (was ₹{breakdown.grandTotal.toLocaleString()})
                                </div>
                              )}
                              <div className={styles.priceLabel}>
                                {(isB2bCustomer || customerGstin) ? 'Total incl. GST' : (
                                  breakdown.discountAmount > 0
                                    ? (breakdown.cappedAtBase ? 'After discount, held at base price' : `After ${currentPct}% discount`)
                                    : 'Estimated Price'
                                )}
                              </div>
                              <div className={styles.flexPriceComparison}>
                                <i className="fa fa-clock-o" /> {getPriceBreakdown(vehicleOptions[selectedVehicle], FLEX_DISCOUNT_PERCENT, isB2bCustomer, !!customerGstin)?.cappedAtBase ? 'Flex booking price:' : '25% Flex booking price:'}
                                <span className={styles.flexPriceValue}>₹{flexPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ===== HOURLY BOOKING FORM ===== */}
      {bookingMode === 'hourly' && !isModifyMode && (
        <div className={styles.contentGrid}>
          {/* Left Column - Hourly Form */}
          <div>
            {/* Customer & Schedule Card */}
            <div className={styles.formCard}>
              <div className={`${styles.cardHeader} ${styles.hourlyHeader}`}>
                <i className="fa fa-clock-o" /> Customer & Schedule
              </div>
              <div className={styles.cardBody}>
                {/* User Search (same as standard) */}
                <div className={styles.formGroup}>
                  <label>User</label>
                  <div className={styles.inputWrapper}>
                    <Input
                      type="text"
                      placeholder="Search by name or phone number"
                      value={userSearch}
                      onChange={(e) => handleUserSearch(e.target.value)}
                      disabled={!!selectedUser}
                    />
                    {selectedUser && (
                      <button className={styles.clearBtn} onClick={clearSelectedUser}>
                        <i className="fa fa-times" />
                      </button>
                    )}
                  </div>
                  {userSuggestions.length > 0 && (
                    <div className={styles.suggestionsList}>
                      {userSuggestions.map((user) => (
                        <button
                          key={user.id}
                          className={styles.suggestionItem}
                          onClick={() => handleUserSelect(user)}
                        >
                          {user.name} ({user.phone}){user.is_b2b ? ' · B2B' : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pickup Time */}
                <div className={styles.formGroup}>
                  <label>When is the equipment needed?</label>
                  <div className={styles.inputWrapper}>
                    <DatePicker
                      selected={pickupTime}
                      onChange={(date) => setPickupTime(date)}
                      showTimeSelect
                      dateFormat="Pp"
                      className="form-control"
                      minDate={new Date()}
                    />
                    <span className={`${styles.bookingTypeBadge} ${styles.hourly}`}>
                      <i className="fa fa-clock-o" style={{ marginRight: '4px' }} />
                      hourly
                    </span>
                  </div>
                </div>

                {/* Location */}
                <div className={styles.formGroup}>
                  <label>Job Site Location</label>
                  <div className={styles.inputWrapper}>
                    <Input
                      type="text"
                      placeholder="Enter job site location or click map"
                      value={pickupAddress?.text || ''}
                      onChange={(e) => setPickupAddress({ ...pickupAddress, text: e.target.value })}
                      onFocus={() => setActiveMarkerIndex(-1)}
                    />
                    <button
                      className={styles.searchBtn}
                      onClick={() => handleSearchLocation(true)}
                      disabled={!pickupAddress?.text}
                    >
                      Search
                    </button>
                  </div>
                  {!isModifyMode && selectedCustomerId && (
                    <div className={styles.recommendationsBlock}>
                      <div className={styles.recommendationsLabel}>Saved addresses (quick pick)</div>
                      {recommendationsLoading ? (
                        <div className={styles.helpText}>Loading saved addresses...</div>
                      ) : (
                        <div className={styles.recommendationsChips}>
                          {recommendedAddresses.length > 0 ? (
                            recommendedAddresses.map((address) => (
                              <button
                                key={`hourly-rec-${address.id}`}
                                type="button"
                                className={styles.recommendationChip}
                                onClick={() => applyRecommendedAddress(address, true)}
                              >
                                <div className={styles.recommendationTopRow}>
                                  <span className={styles.recommendationTitle}>{getRecommendedAddressTitle(address)}</span>
                                  {address.is_primary && (
                                    <span className={styles.primaryBadge}>Home</span>
                                  )}
                                </div>
                                <div className={styles.recommendationMeta}>
                                  <i className="fa fa-map-marker" />
                                  <span>{getRecommendedAddressSubtitle(address) || 'Saved customer address'}</span>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className={styles.helpText}>No saved addresses found for this customer.</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {pickupAddress?.id && (
                    <div className={styles.helpText}>
                      <i className="fa fa-check-circle" style={{ color: '#10b981', marginRight: '4px' }} />
                      Location resolved — equipment pickup &amp; drop at same site
                    </div>
                  )}
                  {/* Inline Address Suggestions */}
                  {addressSuggestions.length > 0 && activeMarkerIndex === -1 && (
                    <div className={styles.inlineSuggestionsCard}>
                      <div className={styles.suggestionsHeader}>
                        <i className="fa fa-map-marker" />
                        <span>Found {addressSuggestions.length} similar addresses - please select one:</span>
                      </div>
                      {addressSuggestions.map((suggestion, idx) => (
                        <div
                          key={suggestion.id || `${suggestion.latitude}-${suggestion.longitude}-${idx}`}
                          className={styles.suggestionItem}
                          onClick={() => applyAddressSuggestion(suggestion, true)}
                        >
                          <strong>{suggestion.locality}</strong>
                          <br />
                          <small style={{ color: '#6c757d' }}>
                            {suggestion.city}, {suggestion.state} - {suggestion.pin_code}
                            <span className={styles.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>
                          </small>
                        </div>
                      ))}
                      <button className={styles.closeBtn} onClick={() => { setAddressSuggestions([]); setError(null); }}>
                        Close Suggestions
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Equipment Selection Card */}
            <div className={styles.formCard} style={{ marginTop: '1.5rem' }}>
              <div className={`${styles.cardHeader} ${styles.hourlyHeader}`}>
                <i className="fa fa-cogs" /> Vehicle / Equipment Type
              </div>
              <div className={styles.cardBody}>
                <div className={styles.equipmentGrid}>
                  {Object.entries(EQUIPMENT_CLASS_MAP).map(([classId, info]) => (
                    <div
                      key={classId}
                      className={`${styles.equipmentCard} ${selectedEquipmentClass === Number(classId) ? styles.selected : ''}`}
                      onClick={() => setSelectedEquipmentClass(Number(classId))}
                    >
                      <div className={styles.equipmentIcon}>
                        <i className={`fa ${info.icon}`} />
                      </div>
                      <div className={styles.equipmentInfo}>
                        <div className={styles.equipmentName}>{info.name}</div>
                        <div className={styles.equipmentCategory}>
                          Class {classId} •
                          {' '}
                          {info.category === 'truck'
                            ? 'Truck / LCV'
                            : info.category === 'jcb'
                              ? 'JCB / Excavator'
                              : 'Crane'}
                        </div>
                      </div>
                      {selectedEquipmentClass === Number(classId) && (
                        <div className={styles.equipmentSelected}>
                          <i className="fa fa-check-circle" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Duration & Pricing Card */}
            <div className={styles.formCard} style={{ marginTop: '1.5rem' }}>
              <div className={`${styles.cardHeader} ${styles.hourlyHeader}`}>
                <i className="fa fa-hourglass-half" /> Duration & Pricing
              </div>
              <div className={styles.cardBody}>
                <div className={styles.formGroup}>
                  <label>Duration (hours)</label>
                  <div className={styles.durationInputRow}>
                    <Input
                      type="number"
                      placeholder="e.g., 2"
                      value={equipmentDuration}
                      onChange={(e) => setEquipmentDuration(e.target.value)}
                      min="0.5"
                      max="72"
                      step="0.5"
                      style={{ maxWidth: '120px' }}
                    />
                    <div className={styles.durationPresets}>
                      {DURATION_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          className={`${styles.presetBtn} ${parseFloat(equipmentDuration) === preset.value ? styles.presetActive : ''}`}
                          onClick={() => setEquipmentDuration(String(preset.value))}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Live Pricing Display */}
                {hourlyPricingLoading && selectedEquipmentClass && equipmentDuration && pickupAddress?.id && (
                  <div className={styles.pricingBreakdown}>
                    <div className={styles.pricingHeader}>
                      <i className="fa fa-spinner fa-spin" />
                      Fetching live pricing...
                    </div>
                  </div>
                )}

                {equipmentPriceEstimate && selectedEquipmentClass && (
                  <div className={styles.pricingBreakdown}>
                    <div className={styles.pricingHeader}>
                      <i className="fa fa-calculator" />
                      Price Estimate
                    </div>
                    <div className={styles.pricingBody}>
                      <div className={styles.pricingRow}>
                        <span>Equipment</span>
                        <span>{EQUIPMENT_CLASS_MAP[selectedEquipmentClass]?.name}</span>
                      </div>
                      <div className={styles.pricingRow}>
                        <span>Rate</span>
                        <span>₹{equipmentPriceEstimate.rate_per_hour}/hr</span>
                      </div>
                      <div className={styles.pricingRow}>
                        <span>Duration</span>
                        <span>{equipmentDuration} hour{parseFloat(equipmentDuration) !== 1 ? 's' : ''}</span>
                      </div>
                      {!equipmentPriceEstimate.is_full_day && equipmentPriceEstimate.base_price > 0 && (
                        <div className={styles.pricingRow}>
                          <span>Base / Mobilization</span>
                          <span>₹{equipmentPriceEstimate.base_price}</span>
                        </div>
                      )}
                      {equipmentPriceEstimate.is_full_day && (
                        <div className={styles.pricingNote}>
                          <i className="fa fa-info-circle" />
                          Full day rate applied ({FULL_DAY_HOURS}+ hours) — no base charge
                        </div>
                      )}
                      <div className={`${styles.pricingRow} ${styles.pricingTotal}`}>
                        <span>Total Estimate</span>
                        <span>₹{equipmentPriceEstimate.total.toLocaleString()}</span>
                      </div>
                      {normalizeDiscountPercent(bookingDiscountPercent) > 0 && (
                        <>
                          <div className={styles.pricingRow}>
                            <span>Discount</span>
                            <span>-{normalizeDiscountPercent(bookingDiscountPercent)}%</span>
                          </div>
                          <div className={`${styles.pricingRow} ${styles.pricingTotal}`}>
                            <span>After Discount</span>
                            <span>
                              ₹{applyDiscountToAmount(equipmentPriceEstimate.total, bookingDiscountPercent, 0.0).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                      <div className={`${styles.pricingRow} ${styles.flexPricingRow}`}>
                        <span><i className="fa fa-clock-o" /> 25% Flex price</span>
                        <span className={styles.flexPriceValue}>
                          ₹{applyDiscountToAmount(equipmentPriceEstimate.total, FLEX_DISCOUNT_PERCENT, 0.0).toLocaleString()}
                        </span>
                      </div>
                      <div className={styles.pricingRow} style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e9ecef' }}>
                        <span style={{ fontWeight: 600 }}>Final Amount (editable)</span>
                        <div className={styles.editablePriceWrapper} style={{ justifyContent: 'flex-end' }}>
                          <span className={styles.currencySymbol}>₹</span>
                          <input
                            type="number"
                            className={`${styles.editablePriceInput} ${manualAmountOverride !== '' ? styles.overridden : ''}`}
                            value={manualAmountOverride}
                            onChange={(e) => setManualAmountOverride(e.target.value)}
                            placeholder={applyDiscountToAmount(equipmentPriceEstimate.total, bookingDiscountPercent, 0.0).toLocaleString()}
                            title="Modify the final amount before confirming"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Remarks */}
                <div className={styles.formGroup} style={{ marginTop: '1.25rem' }}>
                  <label>Remarks / Special Instructions</label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Any special instructions for the equipment operator..."
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Materials / Load Description</label>
                  <Input
                    type="text"
                    value={materialDescription}
                    onChange={(e) => setMaterialDescription(e.target.value)}
                    placeholder="e.g. Cement bags, Furniture items, Steel rods (approx 2 tons)"
                  />
                  <div className={styles.helpText}>
                    What materials will be transported? This helps the operator prepare.
                  </div>
                </div>

                <div className={styles.formGroup} style={{ marginTop: '1rem' }}>
                  <label>Discount (%)</label>
                  <div className={styles.inputWrapper}>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0"
                      value={bookingDiscountPercent}
                      onChange={(e) => setBookingDiscountPercent(e.target.value)}
                    />
                  </div>
                  <div className={styles.helpText}>
                    {isB2bCustomer && 'B2B customer — '}
                    {customerGstin && !isB2bCustomer && `GSTIN: ${customerGstin} — `}
                    {customerDiscountPercent > 0
                      ? `Account discount: ${customerDiscountPercent}%`
                      : (isB2bCustomer || customerGstin)
                        ? 'No account discount set. 5% GST will be added.'
                        : 'No preloaded customer discount. You can still enter a manual discount for this booking.'}
                  </div>
                </div>

                <div className={styles.actionButtons}>
                  {isDraftMode && (
                    <button
                      className={styles.confirmBtn}
                      onClick={() => handleShowConfirmPopup('confirm_draft')}
                      disabled={loading || hourlyPricingLoading || !selectedUser || !pickupAddress?.id || !selectedEquipmentClass || !equipmentDuration}
                    >
                      {loading ? (
                        <><i className="fa fa-spinner fa-spin" /> Confirming Draft...</>
                      ) : (
                        <><i className="fa fa-check-circle" /> Confirm Booking</>
                      )}
                    </button>
                  )}
                  <button
                    className={isDraftMode ? styles.saveDraftBtn : styles.confirmBtn}
                    onClick={isDraftMode ? handleSaveDraftHourly : () => handleShowConfirmPopup('confirm_hourly')}
                    disabled={loading || hourlyPricingLoading || !selectedUser || !pickupAddress?.id || !selectedEquipmentClass || !equipmentDuration}
                  >
                    {loading ? (
                      <><i className="fa fa-spinner fa-spin" /> {isDraftMode ? 'Updating Draft...' : 'Creating Hourly Booking...'}</>
                    ) : (
                      <><i className={`fa ${isDraftMode ? 'fa-file-text-o' : 'fa-check-circle'}`} /> {isDraftMode ? 'Update Draft' : 'Confirm Hourly Booking'}</>
                    )}
                  </button>
                  {!isDraftMode && (
                    <button
                      className={styles.saveDraftBtn}
                      onClick={handleSaveDraftHourly}
                      disabled={loading || hourlyPricingLoading || !selectedUser || !pickupAddress?.id || !selectedEquipmentClass}
                    >
                      <i className="fa fa-file-text-o" /> Save as Draft
                    </button>
                  )}
                  <button className={styles.cancelBtn} onClick={() => history.push('/app/customers')}>
                    <i className="fa fa-arrow-left" /> Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Map & Summary */}
          <div className={styles.rightColumn}>
            <div className={styles.stickyContainer}>
              <div className={styles.mapCard}>
                <div className={styles.mapHeader}>
                  <i className="fa fa-map-marker" /> Job Site Location
                </div>
                <div className={styles.mapLegend}>
                  <div className={styles.legendItems}>
                    <div className={styles.legendItem}>
                      <span className={`${styles.dot} ${styles.pickup}`}></span>
                      Job Site
                    </div>
                  </div>
                  <span className={`${styles.activeBadge} ${styles.pickup}`}>
                    Setting Job Site
                  </span>
                </div>
                <div className={styles.mapContainer}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={12}
                    center={defaultCenter}
                    onLoad={onMapLoad}
                    onClick={(event) => {
                      const lat = event.latLng.lat();
                      const lng = event.latLng.lng();
                      setPickupMarker({ lat, lng });
                      resolveAndSetAddress({ lat, lng }, true);
                    }}
                    options={{
                      styles: mapStyles,
                      disableDefaultUI: false,
                      zoomControl: true,
                      mapTypeControl: false,
                      scaleControl: true,
                      streetViewControl: false,
                      rotateControl: false,
                      fullscreenControl: true
                    }}
                  >
                    {pickupMarker && (
                      <Marker
                        position={pickupMarker}
                        icon={{
                          url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
                          scaledSize: new window.google.maps.Size(40, 40)
                        }}
                        title="Job Site Location"
                        animation={window.google.maps.Animation.DROP}
                      />
                    )}
                  </GoogleMap>
                </div>
              </div>

              {/* Hourly Booking Summary */}
              {(pickupAddress || selectedEquipmentClass || equipmentPriceEstimate) && (
                <div className={styles.summaryCard}>
                  <div className={`${styles.summaryHeader} ${styles.hourlySummaryHeader}`}>
                    <i className="fa fa-clipboard" /> Hourly Booking Summary
                  </div>
                  <div className={styles.summaryBody}>
                    {selectedUser && (
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>User:</span>
                        <span className={styles.summaryValue}>{userSearch}</span>
                      </div>
                    )}
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Pickup Time:</span>
                      <span className={styles.summaryValue}>{pickupTime.toLocaleString()}</span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Booking Type:</span>
                      <span className={styles.summaryValue}>
                        <span className={`${styles.bookingTypeBadge} ${styles.hourly}`}>
                          <i className="fa fa-clock-o" style={{ marginRight: '4px' }} />
                          HOURLY
                        </span>
                      </span>
                    </div>
                    {pickupAddress && (
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Job Site:</span>
                        <span className={styles.summaryValue}>{pickupAddress.text}</span>
                      </div>
                    )}
                    {selectedEquipmentClass && (
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Vehicle / Equipment:</span>
                        <span className={styles.summaryValue}>{EQUIPMENT_CLASS_MAP[selectedEquipmentClass]?.name}</span>
                      </div>
                    )}
                    {equipmentDuration && (
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Duration:</span>
                        <span className={styles.summaryValue}>{equipmentDuration} hour{parseFloat(equipmentDuration) !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {equipmentPriceEstimate && (
                      <div className={styles.selectedVehicleSummary}>
                        <div className={styles.vehicleInfo}>
                          <div className={styles.vehicleName}>{EQUIPMENT_CLASS_MAP[selectedEquipmentClass]?.name}</div>
                          <div className={styles.vehicleDetails}>
                            ₹{equipmentPriceEstimate.rate_per_hour}/hr •
                            {equipmentDuration}h •
                            {equipmentPriceEstimate.is_full_day ? 'Full Day Rate' : 'Hourly Rate'}
                          </div>
                        </div>
                        <div className={styles.priceInfo}>
                          {(() => {
                            const computedHourly = applyDiscountToAmount(equipmentPriceEstimate.total, bookingDiscountPercent, 0.0);
                            const effectiveHourly = getEffectiveAmount(computedHourly, manualAmountOverride);
                            const isOverridden = manualAmountOverride !== '' && parseFloat(manualAmountOverride) !== computedHourly;
                            const hourlyFlexPrice = applyDiscountToAmount(equipmentPriceEstimate.total, FLEX_DISCOUNT_PERCENT, 0.0);
                            return (
                              <>
                                {normalizeDiscountPercent(bookingDiscountPercent) > 0 && (
                                  <div className={styles.priceLabel}>
                                    Base: ₹{Number(equipmentPriceEstimate.total).toLocaleString()}
                                  </div>
                                )}
                                <div className={styles.editablePriceWrapper}>
                                  <span className={styles.currencySymbol}>₹</span>
                                  <input
                                    type="number"
                                    className={`${styles.editablePriceInput} ${isOverridden ? styles.overridden : ''}`}
                                    value={manualAmountOverride !== '' ? manualAmountOverride : ''}
                                    onChange={(e) => setManualAmountOverride(e.target.value)}
                                    placeholder={computedHourly.toLocaleString()}
                                    title="Click to modify the final amount"
                                  />
                                </div>
                                {isOverridden && (
                                  <div className={`${styles.priceLabel} ${styles.overriddenLabel}`}>
                                    <i className="fa fa-pencil" style={{ marginRight: '4px' }} />
                                    Overridden (was ₹{computedHourly.toLocaleString()})
                                  </div>
                                )}
                                <div className={styles.priceLabel}>
                                  {normalizeDiscountPercent(bookingDiscountPercent) > 0
                                    ? `After ${normalizeDiscountPercent(bookingDiscountPercent)}% discount`
                                    : 'Estimated Price'}
                                </div>
                                <div className={styles.flexPriceComparison}>
                                  <i className="fa fa-clock-o" /> 25% Flex price:
                                  <span className={styles.flexPriceValue}>₹{hourlyFlexPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Booking Confirmation Popup */}
      {showConfirmPopup && (() => {
        const details = getConfirmationDetails();
        const titleMap = {
          confirm: 'Confirm New Booking',
          modify: 'Confirm Modified Booking',
          confirm_draft: 'Confirm Draft Booking',
          confirm_hourly: 'Confirm Hourly Booking',
        };
        const buttonLabelMap = {
          confirm: 'Confirm Booking',
          modify: 'Modify Booking',
          confirm_draft: 'Confirm Booking',
          confirm_hourly: 'Confirm Hourly Booking',
        };
        return (
          <div className={styles.confirmOverlay} onClick={() => setShowConfirmPopup(false)}>
            <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.confirmHeader}>
                <div className={styles.confirmHeaderIcon}>
                  <i className="fa fa-check-circle" />
                </div>
                <div>
                  <h3>{titleMap[confirmPopupType] || 'Confirm Booking'}</h3>
                  <p>Please verify all details before proceeding</p>
                </div>
                <button className={styles.confirmCloseBtn} onClick={() => setShowConfirmPopup(false)}>
                  <i className="fa fa-times" />
                </button>
              </div>

              <div className={styles.confirmBody}>
                <div className={styles.confirmSection}>
                  <h4><i className="fa fa-user" /> Customer</h4>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Customer</span>
                    <span className={styles.confirmValue}>{details.customer}</span>
                  </div>
                </div>

                <div className={styles.confirmSection}>
                  <h4><i className="fa fa-calendar" /> Booking Details</h4>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Booking Type</span>
                    <span className={styles.confirmValue}>
                      <span className={styles.confirmBadge}>{details.bookingType}</span>
                    </span>
                  </div>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Pickup Time</span>
                    <span className={styles.confirmValue}>{details.pickupTime}</span>
                  </div>
                  {details.flexWindow && (
                    <div className={styles.confirmField}>
                      <span className={styles.confirmLabel}>Flex Window</span>
                      <span className={styles.confirmValue}>{details.flexWindow}</span>
                    </div>
                  )}
                  {details.partLoadBoxes && (
                    <div className={styles.confirmField}>
                      <span className={styles.confirmLabel}>Boxes</span>
                      <span className={styles.confirmValue}>{details.partLoadBoxes}</span>
                    </div>
                  )}
                </div>

                <div className={styles.confirmSection}>
                  <h4><i className="fa fa-map-marker" /> Locations</h4>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Pickup</span>
                    <span className={styles.confirmValue}>{details.pickupAddress}</span>
                  </div>
                  {details.dropAddresses.map((addr, i) => (
                    <div key={i} className={styles.confirmField}>
                      <span className={styles.confirmLabel}>Drop {i + 1}</span>
                      <span className={styles.confirmValue}>{addr}</span>
                    </div>
                  ))}
                </div>

                <div className={styles.confirmSection}>
                  <h4><i className="fa fa-truck" /> Vehicle & Pricing</h4>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Vehicle</span>
                    <span className={styles.confirmValue}>{details.vehicle}</span>
                  </div>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Distance</span>
                    <span className={styles.confirmValue}>{details.distance}</span>
                  </div>
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Duration</span>
                    <span className={styles.confirmValue}>{details.duration}</span>
                  </div>
                  {details.toll && (
                    <div className={styles.confirmField}>
                      <span className={styles.confirmLabel}>Toll</span>
                      <span className={styles.confirmValue}>{details.toll}</span>
                    </div>
                  )}
                  {details.discount && (
                    <div className={styles.confirmField}>
                      <span className={styles.confirmLabel}>Discount</span>
                      <span className={styles.confirmValue}>{details.discount}</span>
                    </div>
                  )}
                  <div className={styles.confirmField}>
                    <span className={styles.confirmLabel}>Total Price</span>
                    <span className={styles.confirmPrice}>{details.price}</span>
                  </div>
                </div>

                {(details.remarks || details.material) && (
                  <div className={styles.confirmSection}>
                    <h4><i className="fa fa-info-circle" /> Additional Info</h4>
                    {details.material && (
                      <div className={styles.confirmField}>
                        <span className={styles.confirmLabel}>Material</span>
                        <span className={styles.confirmValue}>{details.material}</span>
                      </div>
                    )}
                    {details.remarks && (
                      <div className={styles.confirmField}>
                        <span className={styles.confirmLabel}>Remarks</span>
                        <span className={styles.confirmValue}>{details.remarks}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.confirmFooter}>
                <button
                  className={styles.confirmCancelBtn}
                  onClick={() => setShowConfirmPopup(false)}
                >
                  <i className="fa fa-arrow-left" /> Go Back & Edit
                </button>
                <button
                  className={styles.confirmProceedBtn}
                  onClick={handlePopupConfirm}
                  disabled={loading}
                >
                  {loading ? (
                    <><i className="fa fa-spinner fa-spin" /> Processing...</>
                  ) : (
                    <><i className="fa fa-check-circle" /> {buttonLabelMap[confirmPopupType] || 'Confirm'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default NewBooking;
